import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { isCanvasEditableTarget } from '../../saas/workspace/contentProduction/canvas/canvasEditableKeyboardGuard'
import { pixelsUrl } from '../../api/client'
import { hexToRgb } from './backgroundPresets'
import { canvasPixelOperationAllowed, require2dContext } from './canvasReadinessGuard'
import { clearImageDataHistory, releaseEditorCanvasSet } from './editorCanvasLifecycle'
import { chooseEditedImageExportFormat, imageDataHasTransparency } from './exportFormatSelection'
import { compressCanvasToMaxBytes } from './imageEditorFileSizeCompress'
import { injectVistoeraExportMetadata } from './vistoeraImageMetadata'
import { getLastPixelLoadTiming, loadHtmlImage, logEditorLoadTiming } from './mediaLoader'
import {
  classifySourceErrorMessage,
  patchEditorDiagnostics,
  type EditorDiagnosticsRef,
} from './editorDiagnostics'
import {
  type CropRect,
  type CropShape,
  applyNumericCropSize,
  cropOutputSize,
  cropRequiresTransparency,
  enforceAspectRatio,
  isAspectLocked,
  isEllipseLike,
  normalizeCropRect,
  rectFromCreateDrag,
  snapToSquareCentered,
} from './cropGeometry'
import {
  type ShapeAnnotation,
  type ShapeBrushType,
  cloneAnnotations,
  drawShapeAnnotation,
  drawShapeAnnotations,
  drawShapeSelectionHighlight,
  geomFromShapeDrag,
  hitTestShapeAnnotation,
  rotateAnnotationClockwise90,
  scaleAnnotation,
  shapeStrokeHitToleranceImgPx,
  translateShapeAnnotation,
} from './shapeBrushGeometry'

export type { CropRect, CropShape, ShapeAnnotation, ShapeBrushType }

/** VistoeraTASK-00111 P42/P43 — the READY invariant, checked (dev-only,
 * `console.error` — NEVER a `throw`, per P43's explicit "must not crash
 * production" instruction) right before `onReady?.()` fires in both mask-
 * init branches below. A violation here means a LATER pixel operation
 * could still hit a real, currently-unguarded bug even though this
 * component just told its caller it's fully ready — exactly the class of
 * defect this task's diagnostics exist to surface early instead of as a
 * downstream `imgedit-*` crash. */
function devAssertReadyInvariant(ic: HTMLCanvasElement, mc: HTMLCanvasElement): void {
  if (!import.meta.env.DEV) return
  const ok = ic.width > 0 && ic.height > 0 && mc.width === ic.width && mc.height === ic.height
  if (!ok) {
    // eslint-disable-next-line no-console
    console.error('[DualPaneCutoutEditor] READY invariant violated', {
      imageCanvasWidth: ic.width,
      imageCanvasHeight: ic.height,
      maskCanvasWidth: mc.width,
      maskCanvasHeight: mc.height,
    })
  }
}

/** `'none'` — Canvas Image Editor Default Tool P0 (VistoeraTASK-00070):
 * a genuinely neutral/view-only tool. Deliberately unhandled by every
 * `if (tool === ...)` branch in this file's pointer handlers (brush/
 * click/rect/lasso/crop all stay exactly as they were — this is a pure
 * "falls through to nothing" addition, not a rewritten dispatch), so a
 * left-side pointerdown/move/up with `tool==='none'` paints nothing,
 * starts no shape/crop, and does not pan (only the *right* pane's
 * always-on drag/wheel — never gated on `tool` — still works). Existing
 * callers (`ChainEditorModal`) never pass this value, so their behavior
 * is byte-for-byte unchanged. */
/** `'eyedropper'` (VistoeraTASK-00085) — a read-only tool: a left-pane
 * pointerdown samples the composited (background tier + color-paint
 * layer, VistoeraTASK-00430) pixel at that source-image coordinate (via
 * the exact same `screenToImage` transform every other tool already
 * uses — P9's canonical mapping, no second coordinate system) and
 * reports it through `onEyedropperSample`, then returns immediately.
 * Deliberately never sets `drawing.current`, never calls
 * `pushHistory()`/`onDirtyChange?.(true)`/`redraw()` — P13's invariant
 * that sampling a color can NEVER mutate the image or the undo stack,
 * unlike every paint/shape/crop tool in this file. */
export type ToolShape = 'none' | 'brush' | 'click' | 'rect' | 'lasso' | 'pan' | 'crop' | 'eyedropper' | 'shape'
export type Polarity = 'add' | 'subtract'
export type BgMode = 'checker' | 'white' | 'black' | 'gray'
/** Local Color Brush P0 (VistoeraTASK-00080) — orthogonal to `tool`/
 * `polarity`, only meaningful while `tool === 'brush'`:
 *   - `'selection'` (default, byte-for-byte the pre-existing behavior for
 *     every caller that never passes this prop, chiefly `ChainEditorModal`)
 *     — the brush paints the SELECTION MASK only (`polarity` add=keep/
 *     subtract=erase-to-transparent), never touches image pixels.
 *   - `'color'` — the brush paints real `brushColor` pixels directly onto
 *     the editable image canvas (never the mask's white/black "is this
 *     selected" channel) — a plain local paintbrush, not a selection tool.
 *     `polarity` is ignored in this mode (there is no "subtract a color").
 *     The stamped-in color is composited through the SAME image∩mask
 *     pipeline every other tool already draws through (`redrawRight`/
 *     `exportEditedImageBlob`), so it shows up in the result preview and
 *     survives Save As New identically to any other pixel edit — no
 *     parallel render/export path. */
export type BrushMode = 'selection' | 'color'

/** Crop rectangle in SOURCE IMAGE PIXEL coordinates — see `cropGeometry.ts`
 * (VistoeraTASK-00597 extends the original VISTOERA_CANVAS_IMAGE_EDIT_CROP_P0
 * rect with `shape` + `invert`). */

export interface DualPaneEditorHandle {
  exportMaskBlob: () => Promise<Blob>
  /**
   * TASK-00614 — full-canvas RGBA PNG (image RGB × mask alpha), same
   * silhouette math as the right-pane preview. Used by chain transparent
   * save so Render never re-decodes the original + rembg session on the
   * 512MB API box (client compose, API persist — mirrors background/shadow
   * editors). Resolution matches the editor canvases (proxy long-edge cap).
   */
  exportTransparentPngBlob: () => Promise<Blob>
  /** Composites image ∩ mask (identical math to the right-pane preview),
   * then extracts the current crop rect (or the full canvas if no crop
   * is set) — always at the full resolution of the loaded `proxyUrl`
   * image, never a screen/CSS-pixel capture. Used by "另存编辑图片"
   * (Canvas Image Edit save-as-new-asset), never by the existing
   * chain-based `editorSave`/`editorSaveMask` pipeline.
   *
   * VistoeraTASK-00441 — `onStage` is an optional, purely-observational
   * stage-timing hook (never changes behavior/output): called exactly
   * once, synchronously, the instant the full-resolution-source wait
   * (bounded by `EXPORT_FULL_RESOLUTION_WAIT_TIMEOUT_MS`) has settled
   * (resolved, timed out, or genuinely failed) and compositing/encoding
   * is about to start — i.e. the T1_FULL_RESOLUTION_READY /
   * T2_EXPORT_START boundary in that task's own stage-timing contract.
   * The caller (`CanvasImageEditorOverlay.onSaveAsNew`) uses this to
   * derive real `FULL_RESOLUTION_WAIT_MS`/`EXPORT_MS` without this
   * function needing to know anything about timing/reporting itself. */
  exportEditedImageBlob: (onStage?: (stage: 'full_resolution_ready') => void) => Promise<Blob>
  getCropRect: () => CropRect | null
  clearCrop: () => void
  /**
   * VistoeraTASK-00616 / 00618 — set crop width and/or height from the
   * props panel. Max size is the full image; when the requested size no
   * longer fits at the current top-left, x/y shift just enough to stay
   * in-bounds (origin kept when possible). Square/circle stay 1:1.
   * Returns the applied rect, or null if no crop is active.
   */
  setCropSize: (
    size: { w?: number; h?: number },
    opts?: { recordHistory?: boolean },
  ) => CropRect | null
  /**
   * VistoeraTASK-00610 — bake the current crop (same composite math as
   * export/preview) into a new working image; clears crop UI; rebases the
   * undo stack so Undo cannot restore the pre-crop full frame. Reset still
   * recovers the session original. Returns false if no crop / not ready.
   */
  confirmCrop: () => Promise<boolean>
  /** VistoeraTASK-00598 — clear shape-brush annotations only (not crop/mask). */
  clearAnnotations: () => void
  getAnnotationCount: () => number
  undo: () => void
  redo: () => void
  /** VistoeraTASK-00557 — one instant 90° clockwise rotation step,
   * physically rotating the already-loaded canvases in place (never
   * re-fetches/re-downloads the source) and entering the same undo/
   * redo stack as every other edit. No-op before the source has
   * finished loading (`canvasPixelOperationAllowed` guard, same as
   * `invertSelection`/`clearCrop`). */
  rotateClockwise90: () => void
  /**
   * VistoeraTASK-00590 — aspect-locked shrink of every backing canvas
   * (image / mask / color-paint) to ``width``×``height``. Refuses
   * enlargement and identical sizes. Clears any active crop, pushes a
   * history entry (with a background snapshot so Undo can restore the
   * pre-resize pixels), marks dirty, and re-fits the viewport.
   */
  resizeTo: (width: number, height: number) => boolean
  /**
   * VistoeraTASK-00590 — replace the working pixels with a decoded
   * (possibly re-encoded/compressed) image at ``width``×``height``,
   * scaling mask + paint to match. Used by file-size mode after a real
   * encode→decode round-trip so what the editor shows matches what will
   * be saved.
   */
  applyResizedBitmap: (source: CanvasImageSource, width: number, height: number) => boolean
  /**
   * VistoeraTASK-00629 — bind a max export byte ceiling for "另存编辑图片".
   * Survives subsequent edits (re-enforced on export). Pass ``null`` to clear.
   */
  setExportMaxBytes: (maxBytes: number | null) => void
  getExportMaxBytes: () => number | null
  /**
   * VistoeraTASK-00629 — stash the exact compressed Blob from "按文件大小
   * 调整" confirm so an immediate save (no further edits) uploads the same
   * bytes the preview showed. Cleared automatically on the next history
   * push (any further edit).
   */
  setConstrainedExportBlob: (blob: Blob | null) => void
  /** Current working pixel size (1:1 with the backing canvases). */
  getImageSize: () => { w: number; h: number }
  /**
   * Build a same-size composite of image ∩ paint ∩ mask (no crop, no
   * checkerboard) for the resize modal's preview / encode path. Returns
   * ``null`` before the source is ready.
   */
  buildWorkingCompositeCanvas: () => HTMLCanvasElement | null
  invertSelection: () => void
  clearPendingEdits: () => void
  fitToView: () => void
  zoomTo: (pct: number) => void
  canUndo: () => boolean
  canRedo: () => boolean
  /** Canvas Image Editor Ready-State P0 (VistoeraTASK-00070) — re-runs the
   * canonical source load from scratch after a terminal `onSourceError`
   * (see that prop's doc comment). No-op while a load is already in
   * flight (re-entrant-safe: bumping the nonce again just re-triggers the
   * same effect, never a second parallel fetch — the effect's own
   * `cancelled`/key-guard already deduplicates that). */
  retrySourceLoad: () => void
}

interface Props {
  proxyUrl: string
  /** URL of the current mask (working mask, or derived from an existing saved transparent PNG). Null = no mask yet (fully empty selection). */
  maskUrl: string | null
  /**
   * Real `AssetChain` id — when set (the only caller today, `ChainEditorModal`),
   * the actual pixel-loading `Image.src` is built via `pixelsUrl(chainId, kind)`
   * (the static same-origin `/api/media/chain/{id}/{kind}/pixels` proxy)
   * instead of using `proxyUrl`/`maskUrl` directly. Historically the stable
   * `/api/media/chain/{id}/...` URLs 302-redirected to R2 in Cloud mode —
   * incompatible with this editor's Canvas pixel access (a cross-origin
   * redirect drops the real `Origin` header on the follow-up hop, so R2
   * never returns Access-Control-Allow-Origin regardless of its own CORS
   * config). That redirect is gone now (Tenant-Branded Asset Download
   * Delivery P0, 2026-08-25 — `_deliver` streams bytes back same-origin
   * instead), but the dedicated same-origin `/pixels` proxy is still the
   * right choice here: it's this backend's own `CORS_ALLOWED_ORIGINS`
   * config either way, never a storage-provider's. `proxyUrl`/`maskUrl`
   * are still used unchanged as the reload-effect's change-detection key.
   * Omitted by the legacy
   * per-source-image editor (`SourceEditorPage`, different backend pipeline,
   * not part of this fix) — falls back to loading `proxyUrl`/`maskUrl`
   * directly, exactly as before.
   */
  chainId?: string
  /**
   * Progressive display (Object Image Editor UX P0): same-origin `_deliver`
   * URLs (proxy / transparent_proxy) painted as `<img>` immediately while
   * canvas `/pixels` loads in parallel. Never the multi-MB full transparent
   * when a proxy exists. Checkerboard stays on the right host only.
   */
  displayLeftUrl?: string | null
  displayRightUrl?: string | null
  /**
   * VistoeraTASK-00145 — fast, CORS-safe (`DYNAMIC_API`) small pixel URL
   * (e.g. `siteAssetDisplayPreviewPixelsUrl`'s `web` variant) painted onto
   * the FULL-resolution image canvas as soon as it resolves — well before
   * `proxyUrl`'s slower full-original fetch — so the editable surface
   * becomes interactive close to when the progressive `<img>` stand-ins
   * already do, instead of waiting on the full original end-to-end. Only
   * takes effect when `chainId` is unset (the Canvas Image Edit path;
   * `ChainEditorModal`/item-processing never pass this, so their behavior
   * is byte-for-byte unchanged) AND `knownSourceWidth`/`knownSourceHeight`
   * are both provided (without a known true resolution up front, the
   * canvas cannot be sized correctly before ANY image has loaded, so the
   * component falls back to today's single-fetch path). See the
   * source-load effect's doc comment for the full two-tier contract.
   */
  placeholderUrl?: string | null
  /**
   * VistoeraTASK-00145 — the true `original` pixel dimensions, already
   * known to the caller (e.g. `SiteAsset.width`/`height`, fetched as
   * lightweight JSON metadata well before the user ever opens the editor —
   * see `ImageAssetNode`'s `masterMeta`) BEFORE any image bytes for this
   * open have loaded. Lets the image/mask canvases size themselves
   * immediately, so the mask-ready/interactive chain never has to wait on
   * decoding the (possibly huge) full original at all — only on whichever
   * of `placeholderUrl`/`proxyUrl` resolves first. `null`/`undefined` (or
   * either one missing) disables the whole placeholder fast-path — same
   * fallback as `placeholderUrl` above.
   */
  knownSourceWidth?: number | null
  knownSourceHeight?: number | null
  /**
   * VistoeraTASK-00145 — fires `false` at the start of every (re)load that
   * activates the placeholder fast-path, then `true` once the TRUE
   * full-resolution original has actually been painted onto the image
   * canvas (or immediately, and only once, if the placeholder fast-path
   * never activates at all — see call sites).
   *
   * VistoeraTASK-00430 — no longer gates color-brush tool readiness (that
   * now depends only on the generic `pixelsReady`/mask-pipeline-ready
   * state, same as every other tool — see `colorPaintCanvasRef`'s doc
   * comment for why strokes no longer need to wait for the full-
   * resolution original at all). Kept for callers that still want a
   * "has the TRUE original actually arrived yet" signal independent of
   * any specific tool — e.g. the Shell uses it to show a "正在准备原图用于
   * 最终输出" message if "另存编辑图片" is clicked while this is still
   * `false` (`exportEditedImageBlob` always awaits/upgrades to the real
   * original before composing the export regardless of this flag's
   * state, so output resolution is never affected by it either way).
   */
  onFullResolutionReady?: (ready: boolean) => void
  /**
   * Bump this whenever a *new* mask should be (re)loaded even if `maskUrl`'s
   * string value happens to be identical (the backend caches the working
   * mask at a stable per-chain path, so the URL does not change between a
   * fresh auto-cutout and the previous one).
   */
  reloadToken: number
  /**
   * When true AND `maskUrl` is null, the mask canvas initializes fully
   * OPAQUE (whole image "kept"/selected) instead of the historical
   * fully-transparent default. Item-processing's auto-cutout flow always
   * supplies a real `maskUrl` (or omits this prop, defaulting `false`,
   * preserving today's behavior byte-for-byte) — this only matters for a
   * caller with NO auto-cutout step at all (Canvas Image Edit), where
   * starting from "nothing selected" would make the right-pane result
   * preview blank on first open instead of showing the untouched source
   * image (VISTOERA_CANVAS_IMAGE_EDIT_P0 §34 — no auto background
   * removal).
   */
  initialMaskFullyOpaque?: boolean
  tool: ToolShape
  polarity: Polarity
  brushSize: number
  brushHardness: number
  brushOpacity: number
  /** VistoeraTASK-00080 — see `BrushMode`'s doc comment. Defaults to
   * `'selection'`, so `ChainEditorModal` (never passes this prop) is
   * byte-for-byte unaffected. */
  brushMode?: BrushMode
  /** `#rrggbb` — only read while `tool === 'brush' && brushMode === 'color'`.
   * Defaults to `'#ffffff'` (white, the most common product use case per
   * VistoeraTASK-00080 P8 — white-background product photos). Accepts any
   * CSS color string a caller's color picker hands back (normalized via
   * `hexToRgb`, same tolerant parsing `backgroundPresets.ts` already uses
   * for antd `ColorPicker`'s occasional `rgb(...)` `onChange` payload). */
  brushColor?: string
  overlayOpacity: number
  showOverlay: boolean
  bgMode: BgMode
  /** Shows a small on-canvas readout of activeTool/operation/pointer state
   * for verifying the "keep vs remove" op actually flips per §11 of the
   * brush-interaction spec — off by default so it never pollutes the
   * normal UI. */
  debugMode?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onScaleChange?: (scale: number) => void
  onImageSize?: (w: number, h: number) => void
  /** Called once the image + mask for the current (proxyUrl, maskUrl, reloadToken) have finished loading. */
  onReady?: () => void
  /** VistoeraTASK-00137 P2/P6 — fired when the canonical editable source
   * (`loadHtmlImage(proxyUrl)`) has painted the left/right view canvases and
   * `sourceReady` is true — strictly BEFORE mask/history baseline snapshots
   * and `onReady`. Lets the Shell drop the full-pane left spinner while
   * mask/history work continues asynchronously. Never fired for progressive
   * `<img>` stand-ins alone (those can paint from CDN while `/pixels` is
   * still in flight — the exact right-ready/left-loading split this task
   * root-caused). */
  onSourceReady?: () => void
  /**
   * Canvas Image Editor Ready-State P0 (VistoeraTASK-00070) — fired when
   * the canonical source load (`loadHtmlImage(proxyUrl)`, the one that
   * actually feeds the editable image/mask canvases — NOT the
   * `displayLeftUrl`/`displayRightUrl` progressive `<img>` stand-ins,
   * which use a plain `<img src>` and so can keep showing pixels fine
   * even while this fails) terminally fails after
   * `mediaLoader.ts`'s own internal retries are exhausted. Called with
   * `null` at the start of every (re)load attempt — including a manual
   * `retrySourceLoad()` — so a caller's own error banner clears the
   * instant a retry begins, never lingering stale next to a fresh
   * spinner. This is the ONLY way this component ever leaves its
   * "loading" state without becoming ready — no infinite spinner on a
   * real failure (VISTOERA_CANVAS_IMAGE_EDIT_READY_P0 §9). */
  onSourceError?: (message: string | null) => void
  /**
   * Fired at most once per (tool, polarity) activation when the user does a
   * pointerdown-then-drag while `tool === 'click'` — i.e. tries to "paint"
   * with a single-point AI-prompt tool, which by design only ever places
   * one point on pointerdown and never continues on pointermove. Purely a
   * UX signal for the caller to show a hint; does not change any painting
   * behavior itself.
   */
  onClickToolDragAttempt?: () => void
  /** Fired whenever the crop rect changes (create/move/resize/clear/undo/
   * redo) — purely a UX signal for the caller's toolbar (e.g. enabling a
   * "reset crop" affordance). Never required for the crop math itself,
   * which is fully self-contained in this component. */
  onCropChange?: (rect: CropRect | null) => void
  /**
   * VistoeraTASK-00597 — active crop shape for NEW create-drags and for
   * live meta updates when a crop already exists. Defaults to `'rect'`
   * (free rectangle) so ChainEditorModal / callers that never pass this
   * keep the historical free-rect crop behavior.
   */
  cropShape?: CropShape
  /**
   * VistoeraTASK-00597 — invert flag for NEW create-drags / live meta
   * updates. Defaults to `false` (keep interior). Orthogonal to the
   * toolbar "反选" mask invert (`invertSelection`).
   */
  cropInvert?: boolean
  /** VistoeraTASK-00597 — crop selection border stroke color (CSS). */
  cropBorderColor?: string
  /** VistoeraTASK-00597 — crop overlay dim opacity 0..1 (outside or punched region). */
  cropSelectionOpacity?: number
  /**
   * VistoeraTASK-00598 — shape-brush drawing params (only read while
   * `tool === 'shape'`). Defaults preserve no-op for ChainEditorModal.
   */
  shapeBrushType?: ShapeBrushType
  shapeStrokeColor?: string
  shapeStrokeWidth?: number
  shapeFillEnabled?: boolean
  shapeFillColor?: string
  /** Fill opacity 0..1 (UI shows 0–100%). */
  shapeFillOpacity?: number
  onAnnotationsChange?: (count: number) => void
  /** VistoeraTASK-00085 — fired on a left-pane pointerdown while
   * `tool==='eyedropper'` with the sampled RAW source pixel (see
   * `ToolShape`'s `'eyedropper'` doc comment). `null` means the sampled
   * pixel is fully transparent (`alpha === 0`) — P11: never fabricate an
   * RGB from a transparent pixel (that would just be whatever `bgMode`'s
   * preview-only fill happens to be), the caller should show a lightweight
   * "transparent area" hint and leave the current brush color untouched. */
  onEyedropperSample?: (sample: { r: number; g: number; b: number; a: number } | null) => void
  /** VistoeraTASK-00610 — fires whenever undo/redo availability may change. */
  onHistoryChange?: (state: { canUndo: boolean; canRedo: boolean }) => void
  /** VistoeraTASK-00085 P1 — overrides the right pane's `dual-pane-label`
   * text (default: `t('cutoutEditor.transparentPreviewLabel')`, i.e.
   * "透明结果预览" — byte-for-byte unchanged for `ChainEditorModal`, which
   * never passes this prop and whose cutout-focused workflow that label
   * still correctly describes). `CanvasImageEditorOverlay` passes
   * "结果预览"/"Result Preview" — the right pane is now the final
   * composite of ANY local edit (transparent erase, color brush, crop),
   * not just a transparency cutout. */
  resultPaneLabel?: string
  /** VistoeraTASK-00111 P7/P13-P17 — optional structured diagnostics ref.
   * `undefined`/`null` is completely safe (every `patchEditorDiagnostics`
   * call below is a no-op guarded by `if (!ref)`), so existing callers
   * (`ChainEditorModal`) that never pass this prop are byte-for-byte
   * unaffected. */
  diagnosticsRef?: EditorDiagnosticsRef | null
}

const MIN_SCALE = 0.05
const MAX_SCALE = 16
const MIN_VISIBLE_PX = 40
const OVERLAY_COLOR = '235, 70, 90'
/** VistoeraTASK-00432 — bound on `exportEditedImageBlob`'s wait for the
 * full-resolution original (`fullResPromiseRef`) before it falls back to
 * exporting at whatever tier is already painted. See that call site's own
 * doc comment for the full forensic rationale (R2 CORS gap forcing the
 * full-res leg through a slow same-origin Render relay). Generous enough
 * to virtually never trigger for a healthy fast load, short enough that a
 * save can never again appear to hang for literal minutes. */
const EXPORT_FULL_RESOLUTION_WAIT_TIMEOUT_MS = 8000

const DualPaneCutoutEditor = forwardRef<DualPaneEditorHandle, Props>(function DualPaneCutoutEditor(
  {
    proxyUrl,
    maskUrl,
    initialMaskFullyOpaque = false,
    chainId,
    displayLeftUrl,
    displayRightUrl,
    placeholderUrl,
    knownSourceWidth,
    knownSourceHeight,
    onFullResolutionReady,
    reloadToken,
    tool,
    polarity,
    brushSize,
    brushHardness,
    brushOpacity,
    brushMode = 'selection',
    brushColor = '#ffffff',
    overlayOpacity,
    showOverlay,
    bgMode,
    debugMode,
    onDirtyChange,
    onScaleChange,
    onImageSize,
    onReady,
    onSourceReady,
    onSourceError,
    onClickToolDragAttempt,
    onCropChange,
    cropShape = 'rect',
    cropInvert = false,
    cropBorderColor = '#3b82f6',
    cropSelectionOpacity = 0.6,
    shapeBrushType = 'rect',
    shapeStrokeColor = '#ff4d4f',
    shapeStrokeWidth = 8,
    shapeFillEnabled = false,
    shapeFillColor = '#ff4d4f',
    shapeFillOpacity = 0.3,
    onAnnotationsChange,
    onEyedropperSample,
    onHistoryChange,
    resultPaneLabel,
    diagnosticsRef,
  },
  ref,
) {
  const { t } = useTranslation(['autoProcess', 'canvas'])
  const leftHostRef = useRef<HTMLDivElement>(null)
  const rightHostRef = useRef<HTMLDivElement>(null)
  const imageCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))
  const maskCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))
  /** VistoeraTASK-00430 — dedicated layer for `brushMode==='color'` strokes
   * ONLY, kept always 1:1 in pixel dimensions with `imageCanvasRef`/
   * `maskCanvasRef` (sized together in `initCanvasesTo`, never resized on
   * its own). Before this task, `paintColorDot` wrote `brushColor` pixels
   * directly onto `imageCanvasRef` — the SAME canvas the placeholder->full
   * background-tier upgrade (see `paintImageTier`) swaps wholesale. That
   * forced an all-or-nothing choice once any color stroke committed
   * (`colorEditedRef`): either silently keep the WHOLE image frozen at
   * placeholder quality forever (the historical TASK-00145 choice — "never
   * a silently downgraded edit"), or let a later full-resolution swap wipe
   * the user's strokes. Painting strokes onto this SEPARATE, sparse
   * (mostly-transparent) RGBA layer instead removes the dilemma entirely:
   * `imageCanvasRef` stays a pure, freely-swappable "background tier"
   * (placeholder now, full resolution later, swapped via plain
   * `drawImage` with no mask/paint-layer involvement at all), and this
   * layer composites on top at `redrawLeft`/`redrawRight`/
   * `exportEditedImageBlob` time via ordinary `source-over` — exactly like
   * `maskCanvasRef` already composites independently of which image tier
   * is loaded. Also means color-brush strokes no longer need to WAIT for
   * the full-resolution original at all (see `onLeftPointerDown`'s removed
   * `imageQualityTierRef.current !== 'full'` backstop) — they paint
   * straight onto this canvas, which is sized correctly (to the TRUE
   * `knownSourceWidth`/`knownSourceHeight`) from the very first frame,
   * same as `maskCanvasRef` always has been. */
  const colorPaintCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))
  const leftViewRef = useRef<HTMLCanvasElement>(null)
  const rightViewRef = useRef<HTMLCanvasElement>(null)
  const leftProgRef = useRef<HTMLImageElement>(null)
  const rightProgRef = useRef<HTMLImageElement>(null)
  /** VistoeraTASK-00629 — max export bytes from "按文件大小调整"; enforced
   * again on `exportEditedImageBlob` so preview and 另存 cannot diverge. */
  const exportMaxBytesRef = useRef<number | null>(null)
  /** Exact compressed Blob from the last file-size confirm (cleared on next history push). */
  const constrainedExportBlobRef = useRef<Blob | null>(null)

  const [imgSize, setImgSize] = useState({ w: 0, h: 0 })
  const [scale, setScaleState] = useState(1)
  const [offset, setOffsetState] = useState({ x: 0, y: 0 })
  const [sourceReady, setSourceReady] = useState(false)
  const [pixelsReady, setPixelsReady] = useState(false)
  const scaleRef = useRef(1)
  const offsetRef = useRef({ x: 0, y: 0 })
  /** When true, next host ResizeObserver tick should re-run fit-to-view
   * (Object Image Editor Interaction Regression P0): progressive first-paint
   * + parallel /pixels often finish fitToView while the modal body height is
   * still settling, leaving the shared transform sized for a short viewport
   * — which reads as a blank/white lower half once the host grows. */
  const pendingFitRef = useRef(false)
  const imgSizeRef = useRef({ w: 0, h: 0 })
  useEffect(() => {
    imgSizeRef.current = imgSize
  }, [imgSize])
  /** VistoeraTASK-00557 — cumulative clockwise rotation currently baked
   * into `imageCanvasRef`/`maskCanvasRef`/`colorPaintCanvasRef`'s actual
   * pixel content, one of `0 | 90 | 180 | 270`. Reset to `0` at the start
   * of every (re)load (fresh source = always unrotated). Read directly
   * inside `pushHistory` so every history entry — including every
   * existing, unrelated call site — automatically carries whatever
   * rotation was in effect at that point with zero changes to those call
   * sites (§八's "do not build a separate, parallel rotation-history
   * system"). */
  const rotationRef = useRef(0)

  const setScale = (s: number) => {
    scaleRef.current = s
    setScaleState(s)
    onScaleChange?.(s)
  }
  /** Keeps the image from being dragged/zoomed completely out of the
   * viewport: at least `MIN_VISIBLE_PX` of it must always overlap. Applied
   * uniformly to every offset write (pan, wheel-zoom, fit, zoomTo) so the
   * shared viewport state can never end up out of bounds regardless of the
   * call site. */
  const clampOffset = (s: number, o: { x: number; y: number }) => {
    const host = leftHostRef.current
    if (!host || !imgSize.w) return o
    const cw = host.clientWidth
    const ch = host.clientHeight
    if (!cw || !ch) return o
    const iw = imgSize.w * s
    const ih = imgSize.h * s
    const clampAxis = (pos: number, extent: number, viewport: number) => {
      const lo = Math.min(MIN_VISIBLE_PX - extent, viewport - MIN_VISIBLE_PX)
      const hi = Math.max(MIN_VISIBLE_PX - extent, viewport - MIN_VISIBLE_PX)
      return Math.min(Math.max(pos, lo), hi)
    }
    return { x: clampAxis(o.x, iw, cw), y: clampAxis(o.y, ih, ch) }
  }
  const setOffset = (o: { x: number; y: number }) => {
    const clamped = clampOffset(scaleRef.current, o)
    offsetRef.current = clamped
    setOffsetState(clamped)
  }

  /** Each history entry snapshots BOTH the mask ImageData and the crop
   * rect that was active at that point, so undo/redo moves through crop
   * create/move/resize exactly like it already does for brush/rect/lasso
   * edits — one shared stack, not a second parallel one (VISTOERA_
   * CANVAS_IMAGE_EDIT_CROP_P0 §24). For every pre-existing caller (the
   * chain-based ChainEditorModal), `crop` is always `null` in every
   * entry since that caller never sets `tool==='crop'` — byte-for-byte
   * unchanged undo/redo behavior for that flow.
   *
   * `image` (VistoeraTASK-00080, Local Color Brush) — `null` for every
   * entry that did NOT touch the image canvas (every pre-existing
   * mask-only tool: selection brush, rect, lasso, crop, invert — still
   * `null` for 100% of `ChainEditorModal`'s history, so its undo/redo
   * memory footprint is completely unchanged). Only a completed
   * `brushMode==='color'` stroke snapshots the image canvas here.
   * Deliberately NOT snapshotted on every push (that would double the
   * getImageData/memory cost of every brush stroke, including every
   * existing selection-only caller, for a feature only color-brush
   * needs) — `restoreImageStateFor` below walks backward to the nearest
   * entry that DOES carry one (or the baseline) whenever a mask-only
   * entry is restored, which is always correct: a `null` entry can only
   * mean "the image canvas was unchanged by this edit", never "unknown".
   */
  /** `rotation` (VistoeraTASK-00557) — the cumulative clockwise rotation
   * (`0 | 90 | 180 | 270`) that was in effect when THIS entry's `mask`/
   * `image` snapshots were captured — i.e. their actual `ImageData.width`/
   * `.height`. Every pre-existing entry/call site implicitly has
   * `rotation === 0` forever (rotation never changes unless the new
   * rotate action runs), so this is purely additive. `undo`/`redo` use it
   * to detect a rotation-boundary crossing and resize the live canvases
   * to match BEFORE `putImageData` — `putImageData` never resizes a
   * canvas itself (DOM spec), so skipping this when dimensions actually
   * differ would silently misplace/clip pixels.
   *
   * `background` (VistoeraTASK-00590) — optional snapshot of
   * `imageCanvasRef` itself. Normally `null` (color-brush strokes live
   * on `image`/`colorPaintCanvasRef`; the background tier is freely
   * swappable). Populated for resize history entries so Undo across a
   * dimension change can restore the pre-resize pixels — scaling back
   * UP would be lossy and is never used as an undo strategy. */
  type HistoryEntry = {
    mask: ImageData
    image: ImageData | null
    background: ImageData | null
    crop: CropRect | null
    rotation: number
    /** VistoeraTASK-00598 — vector shape annotations (empty for legacy entries). */
    annotations: ShapeAnnotation[]
  }
  const history = useRef<HistoryEntry[]>([])
  const historyIdx = useRef(-1)
  const baselineData = useRef<HistoryEntry | null>(null)
  /** VistoeraTASK-00610 — session original for Reset only; never overwritten by confirmCrop. */
  const sessionOriginalRef = useRef<HistoryEntry | null>(null)
  /** After confirmCrop, refuse pristine full-res swaps that would wipe the working image. */
  const workingImageLockedRef = useRef(false)
  const onHistoryChangeRef = useRef(onHistoryChange)
  onHistoryChangeRef.current = onHistoryChange
  const emitHistoryChange = useCallback(() => {
    onHistoryChangeRef.current?.({
      canUndo: historyIdx.current > 0,
      canRedo: historyIdx.current >= 0 && historyIdx.current < history.current.length - 1,
    })
  }, [])

  const drawing = useRef(false)
  const lastPoint = useRef<{ x: number; y: number } | null>(null)
  const rectStart = useRef<{ x: number; y: number } | null>(null)
  const lassoPoints = useRef<Array<{ x: number; y: number }>>([])
  const panning = useRef(false)
  const rightPanning = useRef(false)

  // --- Crop tool state (additive — see `CropRect` doc comment) ---
  const cropRef = useRef<CropRect | null>(null)
  const [cropVersion, setCropVersion] = useState(0) // bumps to force a redraw when cropRef mutates
  type CropDragMode = 'create' | 'move' | 'resize-n' | 'resize-s' | 'resize-e' | 'resize-w' | 'resize-ne' | 'resize-nw' | 'resize-se' | 'resize-sw'
  const cropDrag = useRef<{
    mode: CropDragMode
    startImg: { x: number; y: number }
    origRect: CropRect | null
    lastImg: { x: number; y: number }
  } | null>(null)
  // VistoeraTASK-00504 — black-crop race guard. `exportEditedImageBlob`
  // below reads `cropRef.current`/the mask+image canvases AFTER an
  // unbounded-ish `await` (the full-resolution wait, documented there as
  // able to take "on the order of minutes" for a large original). Until
  // this task, nothing stopped the user from starting a brand-new crop
  // drag, brush stroke, or undo/redo DURING that exact window — so the
  // pixels/geometry actually read back and encoded into the exported
  // Blob could silently drift away from what was on-screen the instant
  // "另存编辑图片" was clicked. Set `true` for the live duration of any
  // export, read-only by `onLeftPointerDown` to refuse starting a NEW
  // edit while one is in flight (never touches what brush/mask/crop
  // themselves compute — only gates WHEN a new stroke/drag may begin).
  const exportInFlightRef = useRef(false)
  // VistoeraTASK-00597 — live Shift + shape/invert props (refs so pointer
  // handlers and mid-drag key listeners always see the latest value
  // without re-binding).
  const shiftHeldRef = useRef(false)
  const cropShapeRef = useRef<CropShape>(cropShape)
  const cropInvertRef = useRef(cropInvert)
  const cropBorderColorRef = useRef(cropBorderColor)
  const cropSelectionOpacityRef = useRef(cropSelectionOpacity)
  cropShapeRef.current = cropShape
  cropInvertRef.current = cropInvert
  cropBorderColorRef.current = cropBorderColor
  cropSelectionOpacityRef.current = cropSelectionOpacity
  // VistoeraTASK-00598 — shape-brush annotation list + live drag preview.
  const annotationsRef = useRef<ShapeAnnotation[]>([])
  const [annotationVersion, setAnnotationVersion] = useState(0)
  const pendingShapeRef = useRef<ShapeAnnotation | null>(null)
  // VistoeraTASK-00607 — create vs move: one pointer gesture is exactly one mode.
  const shapeDragRef = useRef<{
    mode: 'create' | 'move'
    startImg: { x: number; y: number }
    lastImg: { x: number; y: number }
    /** move only — annotation id + geometry snapshot at pointerdown */
    annId?: string
    origAnn?: ShapeAnnotation
  } | null>(null)
  /** Hover affordance for shape-tool move cursor (image-space hit). */
  const [shapeHoverMove, setShapeHoverMove] = useState(false)
  // VistoeraTASK-00609 — persistent selection (distinct from hover / drag).
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null)
  const selectedShapeIdRef = useRef<string | null>(null)
  selectedShapeIdRef.current = selectedShapeId
  const [shapeCtxMenu, setShapeCtxMenu] = useState<{ x: number; y: number; annId: string } | null>(null)
  useEffect(() => {
    if (tool !== 'shape') {
      if (shapeHoverMove) setShapeHoverMove(false)
      if (selectedShapeId) setSelectedShapeId(null)
      if (shapeCtxMenu) setShapeCtxMenu(null)
    }
  }, [tool, shapeHoverMove, selectedShapeId, shapeCtxMenu])
  const shapeBrushTypeRef = useRef(shapeBrushType)
  const shapeStrokeColorRef = useRef(shapeStrokeColor)
  const shapeStrokeWidthRef = useRef(shapeStrokeWidth)
  const shapeFillEnabledRef = useRef(shapeFillEnabled)
  const shapeFillColorRef = useRef(shapeFillColor)
  const shapeFillOpacityRef = useRef(shapeFillOpacity)
  shapeBrushTypeRef.current = shapeBrushType
  shapeStrokeColorRef.current = shapeStrokeColor
  shapeStrokeWidthRef.current = shapeStrokeWidth
  shapeFillEnabledRef.current = shapeFillEnabled
  shapeFillColorRef.current = shapeFillColor
  shapeFillOpacityRef.current = shapeFillOpacity
  const setAnnotations = (list: ShapeAnnotation[]) => {
    annotationsRef.current = list
    setAnnotationVersion((v) => v + 1)
    onAnnotationsChange?.(list.length)
  }
  const setCropRect = (r: CropRect | null) => {
    const normalized = r ? normalizeCropRect(r) : null
    cropRef.current = normalized
    setCropVersion((v) => v + 1)
    onCropChange?.(normalized)
  }
  const [rightDragging, setRightDragging] = useState(false)
  const panStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 })
  const spaceDown = useRef(false)
  const activePointerId = useRef<number | null>(null)
  // --- Click-tool ("AI point prompt") drag-misuse detection. Purely a UX
  // signal (see `onClickToolDragAttempt` prop docs) — does not touch the
  // brush/rect/lasso pointer branches or painting logic at all. ---
  const clickDownScreenPos = useRef<{ x: number; y: number } | null>(null)
  const clickDragHintFired = useRef(false)
  useEffect(() => {
    clickDragHintFired.current = false
  }, [tool, polarity])
  /** Screen position (relative to the left host's top-left) of the mouse,
   * used only to position the brush-cursor overlay — never for painting
   * math, which always goes through `screenToImage`. Deliberately NOT
   * cleared when the pointer leaves the canvas (e.g. to click a toolbar
   * button or drag the brush-size slider): it just freezes at the last
   * known spot so switching tools / adjusting brush settings never makes
   * the circle "disappear", and its diameter still updates live from
   * `brushSize`/`scale` even while the mouse is over the properties
   * panel. It is only ever reset to `null` before the very first move. */
  const [brushCursorPos, setBrushCursorPos] = useState<{ x: number; y: number } | null>(null)
  const [debugInfo, setDebugInfo] = useState({
    tool: '' as string,
    polarity: '' as string,
    drawing: false,
    pointerId: null as number | null,
    coalesced: 0,
  })

  const redrawLeft = useCallback(() => {
    const vc = leftViewRef.current
    const ic = imageCanvasRef.current
    const mc = maskCanvasRef.current
    if (!vc || !imgSize.w) return
    const ctx = vc.getContext('2d')!
    ctx.clearRect(0, 0, vc.width, vc.height)
    ctx.drawImage(ic, 0, 0)
    // VistoeraTASK-00430 — color-brush strokes live on their own layer
    // now (see `colorPaintCanvasRef`'s doc comment); a plain `source-over`
    // draw composites them on top of whichever background tier `ic`
    // currently holds, in the exact same visual position they occupied
    // when painted directly onto `ic` (byte-for-byte same stacking order
    // relative to the selection-tint overlay below).
    ctx.drawImage(colorPaintCanvasRef.current, 0, 0)
    if (showOverlay) {
      const tmp = document.createElement('canvas')
      tmp.width = vc.width
      tmp.height = vc.height
      const tctx = tmp.getContext('2d')!
      tctx.fillStyle = `rgb(${OVERLAY_COLOR})`
      tctx.fillRect(0, 0, vc.width, vc.height)
      tctx.globalCompositeOperation = 'destination-in'
      tctx.drawImage(mc, 0, 0)
      ctx.globalAlpha = overlayOpacity
      ctx.drawImage(tmp, 0, 0)
      ctx.globalAlpha = 1
      // thin edge highlight around the selection boundary — skipped while
      // actively dragging a brush/shape to keep painting responsive.
      if (!drawing.current) drawMaskEdge(ctx, mc)
    }
    if (tool === 'rect' && rectStart.current && lastPoint.current) {
      drawRectPreview(ctx, rectStart.current, lastPoint.current)
    }
    if (tool === 'lasso' && lassoPoints.current.length > 1) {
      drawLassoPreview(ctx, lassoPoints.current)
    }
    if (tool === 'crop' && cropRef.current) {
      drawCropOverlay(ctx, cropRef.current, vc.width, vc.height, scaleRef.current, {
        borderColor: cropBorderColorRef.current,
        selectionOpacity: cropSelectionOpacityRef.current,
      })
    }
    // VistoeraTASK-00598 — annotations above selection tint so they stay readable.
    drawShapeAnnotations(ctx, annotationsRef.current)
    if (pendingShapeRef.current) drawShapeAnnotation(ctx, pendingShapeRef.current)
    // VistoeraTASK-00609 — interaction-only selection halo (never exported).
    const selId = selectedShapeIdRef.current
    if (selId) {
      const sel = annotationsRef.current.find((a) => a.id === selId)
      if (sel) drawShapeSelectionHighlight(ctx, sel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgSize.w, overlayOpacity, showOverlay, tool, cropVersion, cropBorderColor, cropSelectionOpacity, annotationVersion, selectedShapeId])

  const redrawRight = useCallback(() => {
    const vc = rightViewRef.current
    const ic = imageCanvasRef.current
    const mc = maskCanvasRef.current
    if (!vc || !imgSize.w) return
    const ctx = vc.getContext('2d')!
    ctx.clearRect(0, 0, vc.width, vc.height)
    if (bgMode !== 'checker') {
      ctx.fillStyle = bgMode === 'white' ? '#ffffff' : bgMode === 'black' ? '#000000' : '#808080'
      ctx.fillRect(0, 0, vc.width, vc.height)
    }
    const tmp = document.createElement('canvas')
    tmp.width = vc.width
    tmp.height = vc.height
    const tctx = tmp.getContext('2d')!
    tctx.drawImage(ic, 0, 0)
    // VistoeraTASK-00430 — see `redrawLeft`'s matching comment: color-brush
    // strokes composite on top of the background tier before the mask cut,
    // so the result preview shows the painted color exactly like it did
    // when strokes were baked directly into `ic`.
    tctx.drawImage(colorPaintCanvasRef.current, 0, 0)
    tctx.globalCompositeOperation = 'destination-in'
    tctx.drawImage(mc, 0, 0)
    // VistoeraTASK-00598 — bake annotations onto the composited result
    // (after mask cut, before crop) so export/preview match.
    tctx.globalCompositeOperation = 'source-over'
    drawShapeAnnotations(tctx, annotationsRef.current)
    if (pendingShapeRef.current) drawShapeAnnotation(tctx, pendingShapeRef.current)
    {
      const selId = selectedShapeIdRef.current
      if (selId) {
        const sel = annotationsRef.current.find((a) => a.id === selId)
        if (sel) drawShapeSelectionHighlight(tctx, sel)
      }
    }
    ctx.drawImage(tmp, 0, 0)
    // Crop preview (P19/P23 + VistoeraTASK-00597): shape-aware clip.
    // Non-invert → destination-in the shape (outside transparent).
    // Invert → destination-out punch (interior transparent, full frame).
    if (cropRef.current) {
      ctx.save()
      applyCropShapeComposite(ctx, cropRef.current)
      ctx.restore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgMode, imgSize.w, cropVersion, annotationVersion, selectedShapeId])

  const redraw = useCallback(() => {
    redrawLeft()
    redrawRight()
  }, [redrawLeft, redrawRight])

  // During an active brush stroke, `paintDot` calls (cheap circle fills)
  // happen synchronously for every coalesced pointer sample, but the full
  // composite (`redraw`, which rebuilds the overlay tint + transparent
  // preview from scratch) is comparatively expensive. Coalescing it to
  // once per animation frame keeps painting responsive even when the
  // browser delivers many pointermove/coalesced samples between frames,
  // and — importantly — keeps the main thread free enough that the
  // brush-cursor overlay's own state update never gets starved.
  const redrawRafId = useRef<number | null>(null)
  const scheduleRedraw = useCallback(() => {
    if (redrawRafId.current != null) return
    redrawRafId.current = requestAnimationFrame(() => {
      redrawRafId.current = null
      redraw()
    })
  }, [redraw])
  useEffect(
    () => () => {
      if (redrawRafId.current != null) cancelAnimationFrame(redrawRafId.current)
    },
    [],
  )

  // VistoeraTASK-00122 — release 4K-class canvas backing stores and undo
  // ImageData snapshots when the editor unmounts (close modal / route away).
  // Without this, Chromium can retain hundreds of MiB per closed session
  // until a full GC cycle, which shows up as STRONG_RETENTION_SIGNAL in
  // repeated open/close stress tests.
  useEffect(() => {
    return () => {
      if (redrawRafId.current != null) cancelAnimationFrame(redrawRafId.current)
      releaseEditorCanvasSet([
        imageCanvasRef.current,
        maskCanvasRef.current,
        colorPaintCanvasRef.current,
        leftViewRef.current,
        rightViewRef.current,
      ])
      clearImageDataHistory(history.current)
      historyIdx.current = -1
      baselineData.current = null
    }
  }, [])

  /** `imageChanged` (VistoeraTASK-00080) — pass `true` only when the
   * completed edit could have touched the image canvas (a `brushMode===
   * 'color'` stroke). Every pre-existing call site (rect/lasso/crop/
   * invert/selection-brush) passes `false` (or omits the arg — see
   * default below), keeping their history entries' `image` field `null`
   * exactly as before this task. */
  const pushHistory = useCallback((imageChanged = false) => {
    const mc = maskCanvasRef.current
    if (!baselineHistoryReadyRef.current) return
    // Editor Stability P0 (VistoeraTASK-00103) — `mc`/`ic` are eagerly
    // created offscreen canvases (`useRef(document.createElement('canvas'))`,
    // never null), but default to 0×0 until the source-load effect assigns
    // real dimensions. `CanvasRenderingContext2D.getImageData()` THROWS an
    // `IndexSizeError` on a zero width/height source rect (DOM spec, not a
    // Vistoera bug) — this was the actual root cause of the intermittent
    // "画布加载失败" Canvas-fatal crash this task forensically root-caused:
    // any pixel-touching interaction that reached the canvas before the
    // source image finished loading (a stray click with an "AI 点选" tool
    // armed, an in-progress brush/rect/lasso stroke, or simply pressing the
    // always-enabled "反选"/invert button) threw this uncaught inside a raw
    // React pointer-event handler, and — with no local Image Editor error
    // boundary anywhere above it — it bubbled all the way up to the root
    // `CanvasErrorBoundary`, taking down the entire Canvas Surface. A no-op
    // here is always correct (there is nothing valid to snapshot yet); the
    // pointer handlers below also gate on `pixelsReady` so in practice this
    // should be unreachable, but this is the actual data-layer invariant —
    // every caller, present and future (`invertSelection`, `clearCrop`, …)
    // — now gets for free, without needing its own bespoke guard. See
    // `canvasPixelOperationAllowed`'s doc comment for the full story.
    if (!canvasPixelOperationAllowed(mc.width, mc.height)) return
    const ctx = mc.getContext('2d')!
    const data = ctx.getImageData(0, 0, mc.width, mc.height)
    let imageSnapshot: ImageData | null = null
    if (imageChanged) {
      // VistoeraTASK-00430 — a real color-brush stroke just committed;
      // snapshot the dedicated paint layer (`colorPaintCanvasRef`), never
      // `imageCanvasRef` any more (that canvas is now a pure, freely-
      // swappable background tier — see that ref's doc comment — undo/
      // redo must only ever touch what the user actually painted).
      const paint = colorPaintCanvasRef.current
      imageSnapshot = paint.getContext('2d')!.getImageData(0, 0, paint.width, paint.height)
    }
    history.current = history.current.slice(0, historyIdx.current + 1)
    // VistoeraTASK-00557 — reads `rotationRef.current` directly rather
    // than taking a parameter, so this one line is the ONLY change
    // needed for every existing call site (brush/crop/click/invert/…) to
    // automatically carry whatever rotation is currently in effect.
    // VistoeraTASK-00590 — `background` stays null for ordinary edits;
    // resize paths pass `captureBackground=true` via the overload below.
    history.current.push({
      mask: data,
      image: imageSnapshot,
      background: null,
      crop: cropRef.current,
      rotation: rotationRef.current,
      annotations: cloneAnnotations(annotationsRef.current),
    })
    if (history.current.length > 30) history.current.shift()
    historyIdx.current = history.current.length - 1
    // VistoeraTASK-00629 — any further edit invalidates the exact
    // file-size-confirm Blob; the max-bytes ceiling itself remains.
    constrainedExportBlobRef.current = null
    emitHistoryChange()
  }, [emitHistoryChange])

  /** VistoeraTASK-00590 — like `pushHistory(true)` but also snapshots
   * `imageCanvasRef` into `background` so Undo across a dimension change
   * can restore the exact pre/post-resize pixels (lossy upscale is never
   * used as an undo strategy). */
  const pushHistoryWithBackground = useCallback(() => {
    const mc = maskCanvasRef.current
    if (!baselineHistoryReadyRef.current) return
    if (!canvasPixelOperationAllowed(mc.width, mc.height)) return
    const ctx = mc.getContext('2d')!
    const data = ctx.getImageData(0, 0, mc.width, mc.height)
    const paint = colorPaintCanvasRef.current
    const imageSnapshot = paint.getContext('2d')!.getImageData(0, 0, paint.width, paint.height)
    const ic = imageCanvasRef.current
    const background = ic.getContext('2d')!.getImageData(0, 0, ic.width, ic.height)
    history.current = history.current.slice(0, historyIdx.current + 1)
    history.current.push({
      mask: data,
      image: imageSnapshot,
      background,
      crop: cropRef.current,
      rotation: rotationRef.current,
      annotations: cloneAnnotations(annotationsRef.current),
    })
    if (history.current.length > 30) history.current.shift()
    historyIdx.current = history.current.length - 1
    // VistoeraTASK-00629 — see matching clear in `pushHistory`.
    constrainedExportBlobRef.current = null
    emitHistoryChange()
  }, [emitHistoryChange])

  /** VistoeraTASK-00609 — delete one annotation; one history entry. */
  const deleteShapeById = useCallback(
    (id: string) => {
      const prev = annotationsRef.current
      const next = prev.filter((a) => a.id !== id)
      if (next.length === prev.length) return
      setAnnotations(next)
      if (selectedShapeIdRef.current === id) setSelectedShapeId(null)
      setShapeCtxMenu(null)
      pushHistory()
      onDirtyChange?.(true)
      redraw()
    },
    [onDirtyChange, pushHistory, redraw],
  )

  /** Snapshot the current background onto the CURRENT history entry if
   * it does not already have one — called immediately BEFORE a resize so
   * the previous step remains restorable after dimensions change. */
  const ensureBackgroundOnCurrentHistory = useCallback(() => {
    const entry = history.current[historyIdx.current]
    if (!entry || entry.background) return
    const ic = imageCanvasRef.current
    if (!canvasPixelOperationAllowed(ic.width, ic.height)) return
    entry.background = ic.getContext('2d')!.getImageData(0, 0, ic.width, ic.height)
  }, [])

  /** VistoeraTASK-00080 (restores the color-paint layer, not the
   * background tier — repurposed VistoeraTASK-00430): walks backward
   * from `idx` for the nearest entry that actually snapshotted a
   * completed color-brush stroke, falling back to the baseline's own
   * (always-blank) snapshot if none is found between `idx` and the start.
   * Correct because a `null` `image` field can only mean "this entry left
   * the paint layer exactly as the previous entry left it" (mask-only
   * tools never touch `colorPaintCanvasRef` at all) — never "unknown/
   * lost". Never touches `imageCanvasRef` — which background tier
   * (placeholder/full) is currently painted is completely independent of
   * undo/redo history (VistoeraTASK-00430: swapping tiers is no longer a
   * user-visible "edit" at all, just a silent quality upgrade). */
  const restoreImageStateFor = useCallback((idx: number) => {
    const paint = colorPaintCanvasRef.current
    for (let i = idx; i >= 0; i -= 1) {
      const snapshot = history.current[i]?.image
      if (snapshot) {
        paint.getContext('2d')!.putImageData(snapshot, 0, 0)
        return
      }
    }
    if (baselineData.current?.image) {
      paint.getContext('2d')!.putImageData(baselineData.current.image, 0, 0)
    } else {
      paint.getContext('2d')!.clearRect(0, 0, paint.width, paint.height)
    }
  }, [])

  const applyTransform = useCallback(() => {
    const t = `translate(${offsetRef.current.x}px, ${offsetRef.current.y}px) scale(${scaleRef.current})`
    // Shared viewport transform must hit BOTH the brush canvases AND the
    // progressive <img> stand-ins — otherwise wheel/pan during the
    // progressive-first-paint window only moves opacity:0 canvases and the
    // visible proxy images stay glued (Interaction Regression P0).
    if (leftViewRef.current) leftViewRef.current.style.transform = t
    if (rightViewRef.current) rightViewRef.current.style.transform = t
    if (leftProgRef.current) leftProgRef.current.style.transform = t
    if (rightProgRef.current) rightProgRef.current.style.transform = t
  }, [])

  useEffect(applyTransform, [scale, offset, applyTransform])

  const fitToView = useCallback(() => {
    const host = leftHostRef.current
    if (!host || !imgSize.w) return
    const pad = 20
    const cw = host.clientWidth || host.getBoundingClientRect().width
    const ch = host.clientHeight || host.getBoundingClientRect().height
    if (!cw || !ch) return
    const sx = (cw - pad) / imgSize.w
    const sy = (ch - pad) / imgSize.h
    const s = Math.max(MIN_SCALE, Math.min(sx, sy, 1))
    setScale(s)
    setOffset({
      x: (cw - imgSize.w * s) / 2,
      y: (ch - imgSize.h * s) / 2,
    })
  }, [imgSize])

  const zoomTo = useCallback(
    (pct: number) => {
      const host = leftHostRef.current
      if (!host || !imgSize.w) return
      const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pct))
      const cx = host.clientWidth / 2
      const cy = host.clientHeight / 2
      const imgX = (cx - offsetRef.current.x) / scaleRef.current
      const imgY = (cy - offsetRef.current.y) / scaleRef.current
      setScale(s)
      setOffset({ x: cx - imgX * s, y: cy - imgY * s })
    },
    [imgSize],
  )

  /** Last host size we fitted against while pendingFit is true. Used to
   * re-fit when the modal/flex host grows after a premature first fit
   * (white lower-half regression). Cleared by settle timer or user zoom/pan. */
  const lastFitHostRef = useRef({ w: 0, h: 0 })
  /** Consecutive ResizeObserver ticks at the same host size while
   * `pendingFitRef` is true — used to end INITIAL_AUTO_FIT without a
   * fixed setTimeout (Transparent Editor First-Open P0). */
  const stableFitFramesRef = useRef(0)

  const tryFitWhenHostReady = useCallback(() => {
    const host = leftHostRef.current
    const { w, h } = imgSizeRef.current
    if (!host || !w || !h) return false
    const cw = host.clientWidth || host.getBoundingClientRect().width
    const ch = host.clientHeight || host.getBoundingClientRect().height
    // Modal / flex layout often reports a stub height on the first paint
    // frame; wait for a real viewport before committing fit.
    if (cw < 80 || ch < 120) return false
    const pad = 20
    const sx = (cw - pad) / w
    const sy = (ch - pad) / h
    const s = Math.max(MIN_SCALE, Math.min(sx, sy, 1))
    setScale(s)
    setOffset({ x: (cw - w * s) / 2, y: (ch - h * s) / 2 })
    lastFitHostRef.current = { w: cw, h: ch }
    return true
  }, [])

  // VistoeraTASK-00414 — Initial Fit regression fix. The single rAF below
  // used to call `applyTransform()` unconditionally even when
  // `tryFitWhenHostReady()` returned `false` (host still reporting a stub
  // size on that exact frame — e.g. the overlay/modal hasn't finished its
  // own layout pass yet). Since `onProgressiveLeftLoad`/
  // `onProgressiveRightLoad` already synchronously size the stand-in
  // `<img>` to its true natural pixel dimensions (no `max-width` cap,
  // deliberately — see `.dual-pane-progressive-img`) BEFORE calling this,
  // applying a transform computed from the *stale* `scale`/`offset`
  // (React state default `1`/`{0,0}` on a fresh mount) painted the
  // untransformed, natural-size image glued to the top-left corner —
  // i.e. exactly the "shows only part of the image, must manually zoom
  // out to ~30%" bug — and recovery depended entirely on the host
  // ResizeObserver firing again, which only happens on an actual size
  // *change*, never on a one-frame "not laid out yet" race that settles
  // on its own without the host dimensions ever changing again.
  // Fix: retry across a few more rAFs (frame-paced, NOT a timer/sleep —
  // bounded at `INITIAL_FIT_RAF_RETRY_BUDGET` frames so a genuinely
  // zero-size/detached host still gives up quickly) until a real fit
  // lands, and only call `applyTransform()` once `tryFitWhenHostReady()`
  // has actually succeeded (or the budget is exhausted, matching the old
  // worst-case behavior as a last-resort fallback — the ResizeObserver
  // safety net still applies on top of this either way).
  const INITIAL_FIT_RAF_RETRY_BUDGET = 30
  const beginInitialFit = useCallback(() => {
    pendingFitRef.current = true
    lastFitHostRef.current = { w: 0, h: 0 }
    stableFitFramesRef.current = 0
    let attempt = 0
    const tick = () => {
      attempt += 1
      if (tryFitWhenHostReady()) {
        applyTransform()
        return
      }
      if (attempt < INITIAL_FIT_RAF_RETRY_BUDGET) {
        requestAnimationFrame(tick)
        return
      }
      // Budget exhausted — fall back to the pre-fix behavior (apply
      // whatever scale/offset exist) rather than leaving the stand-in
      // permanently untransformed; the ResizeObserver remains armed
      // (`pendingFitRef.current` is still `true`) to correct it later.
      applyTransform()
    }
    requestAnimationFrame(tick)
  }, [applyTransform, tryFitWhenHostReady])

  // ------------------------------------------------------------------
  // Source + mask load (decoupled). Source must paint as soon as the proxy
  // `/pixels` leg resolves; mask may arrive later (first-open auto-cutout)
  // without cancelling/re-fetching the source (Transparent Editor First-Open
  // P0, 2026-09-17).
  // ------------------------------------------------------------------

  const sourceKeyRef = useRef('')
  const maskKeyRef = useRef('')
  /** False while initial mask baseline `getImageData` is deferred off the
   * critical path (VistoeraTASK-00137 P10) — `pushHistory` must no-op until
   * true so undo never snapshots before the baseline exists. */
  const baselineHistoryReadyRef = useRef(true)
  /** VistoeraTASK-00145 — `'none'` before anything has painted,
   * `'placeholder'` once the fast small-pixel source has painted the image
   * canvas (upscaled to the true resolution), `'full'` once the genuine
   * full-resolution original has. Only ever set outside the placeholder
   * fast-path (i.e. when `placeholderUrl`/`knownSourceWidth`/
   * `knownSourceHeight` are unavailable) directly to `'full'` — see the
   * source-load effect. VistoeraTASK-00430 — no longer read by
   * `onLeftPointerDown`'s color-brush path (strokes paint onto
   * `colorPaintCanvasRef` regardless of tier now); still read by
   * `exportEditedImageBlob`'s "wait for/upgrade to full resolution before
   * composing the export" step. */
  const imageQualityTierRef = useRef<'none' | 'placeholder' | 'full'>('none')
  /** VistoeraTASK-00145 — the in-flight (or already-resolved) full-
   * resolution `loadHtmlImage(proxyPixelUrl)` promise for the CURRENT load
   * generation, so `exportEditedImageBlob` can `await` it (cheap even if
   * already settled) instead of ever exporting at placeholder resolution
   * while a fast full-res image is available or already loading. `null`
   * outside the placeholder fast-path (the non-placeholder path already
   * cannot reach export before `imageQualityTierRef.current === 'full'`,
   * since `pixelsReady`/`onReady` never fire before that fetch resolves). */
  const fullResPromiseRef = useRef<Promise<HTMLImageElement> | null>(null)
  /** Bumped only by the `retrySourceLoad` imperative method — an
   * additional dependency on the source-loading effect below so a manual
   * retry re-runs it even though `proxyUrl`/`chainId` never changed. */
  const [sourceRetryNonce, setSourceRetryNonce] = useState(0)

  function commitBaselineHistory(captureImageSnapshot: boolean) {
    const mc = maskCanvasRef.current
    // VistoeraTASK-00430 — baseline's `image` field now snapshots the
    // color-paint layer (never `imageCanvasRef`, the freely-swappable
    // background tier). Every call site passes `false` today (no caller
    // starts with pre-existing color-brush strokes), kept as a parameter
    // for shape-parity with `pushHistory`'s own `imageChanged` flag.
    const paint = colorPaintCanvasRef.current
    if (!canvasPixelOperationAllowed(mc.width, mc.height)) return
    const mctx = mc.getContext('2d')!
    annotationsRef.current = []
    pendingShapeRef.current = null
    baselineData.current = {
      mask: mctx.getImageData(0, 0, mc.width, mc.height),
      image: captureImageSnapshot ? paint.getContext('2d')!.getImageData(0, 0, paint.width, paint.height) : null,
      background: null,
      crop: null,
      // VistoeraTASK-00557 — baseline is only ever committed from the
      // fresh-load effects below, before any rotation can occur; always
      // `0`, mirroring `rotationRef`'s own reset at load start.
      rotation: 0,
      annotations: [],
    }
    // VistoeraTASK-00610 — pin session original once per load for Reset.
    if (!sessionOriginalRef.current) {
      sessionOriginalRef.current = baselineData.current
    }
    history.current = [baselineData.current]
    historyIdx.current = 0
    onDirtyChange?.(false)
    baselineHistoryReadyRef.current = true
    emitHistoryChange()
  }

  function scheduleCommitBaselineHistory(captureImageSnapshot: boolean) {
    baselineHistoryReadyRef.current = false
    const run = () => {
      commitBaselineHistory(captureImageSnapshot)
    }
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(run, { timeout: 800 })
    } else {
      setTimeout(run, 0)
    }
  }

  function paintMaskFromImage(mimg: HTMLImageElement) {
    const mc = maskCanvasRef.current
    const mctx = require2dContext(mc, 'paintMaskFromImage')
    mctx.clearRect(0, 0, mc.width, mc.height)
    mctx.drawImage(mimg, 0, 0, mc.width, mc.height)
    const data = mctx.getImageData(0, 0, mc.width, mc.height)
    for (let i = 0; i < data.data.length; i += 4) {
      const a = data.data[i + 3] < 255 ? data.data[i + 3] : data.data[i]
      data.data[i] = 255
      data.data[i + 1] = 255
      data.data[i + 2] = 255
      data.data[i + 3] = a
    }
    mctx.putImageData(data, 0, 0)
    setCropRect(null)
  }

  /** Fills mask fully opaque (Canvas Image Edit default). Baseline history
   * is committed asynchronously — see `scheduleCommitBaselineHistory`. */
  function paintMaskFullyOpaque() {
    const mc = maskCanvasRef.current
    const mctx = require2dContext(mc, 'paintMaskFullyOpaque')
    mctx.clearRect(0, 0, mc.width, mc.height)
    mctx.fillStyle = 'rgba(255,255,255,1)'
    mctx.fillRect(0, 0, mc.width, mc.height)
    setCropRect(null)
  }

  useEffect(() => {
    const key = `${proxyUrl}::${chainId || ''}`
    sourceKeyRef.current = key
    let cancelled = false
    setSourceReady(false)
    setPixelsReady(false)
    baselineHistoryReadyRef.current = false
    imageQualityTierRef.current = 'none'
    fullResPromiseRef.current = null
    // VistoeraTASK-00610 — new source load clears working-image lock + session original.
    workingImageLockedRef.current = false
    sessionOriginalRef.current = null
    setImgSize({ w: 0, h: 0 })
    imgSizeRef.current = { w: 0, h: 0 }
    // VistoeraTASK-00557 — every fresh (re)load starts unrotated, even
    // when reloading the SAME source (`retrySourceLoad`) or opening a
    // brand-new one in an editor instance that was previously rotated.
    rotationRef.current = 0
    // Clears any error banner from a previous failed attempt the instant
    // a (re)load starts — including a manual `retrySourceLoad()` — so the
    // caller's UI never shows a stale error next to a fresh spinner.
    onSourceError?.(null)
    patchEditorDiagnostics(diagnosticsRef, {
      phase: 'LOADING_SOURCE',
      sourceKind: chainId ? 'CHAIN_PIXELS_PROXY' : 'SITE_ASSET_MASTER_PIXELS',
      sourceReady: false,
      pixelsReady: false,
      sourceNaturalWidth: null,
      sourceNaturalHeight: null,
      imageCanvasWidth: 0,
      imageCanvasHeight: 0,
      maskCanvasWidth: 0,
      maskCanvasHeight: 0,
      leftViewCanvasWidth: 0,
      leftViewCanvasHeight: 0,
      rightViewCanvasWidth: 0,
      rightViewCanvasHeight: 0,
      lastSourceErrorClass: null,
    })

    const proxyPixelUrl = chainId ? pixelsUrl(chainId, 'proxy') : proxyUrl
    const t0 = performance.now()

    function initCanvasesTo(w: number, h: number) {
      const ic = imageCanvasRef.current
      const mc = maskCanvasRef.current
      // VistoeraTASK-00430 — the color-paint layer is sized/cleared in
      // lockstep with image/mask, every load, so it is always 1:1 with
      // them (same invariant `devAssertReadyInvariant` already checks for
      // image vs mask) and starts fully blank for a brand-new load.
      const paint = colorPaintCanvasRef.current
      ic.width = w
      ic.height = h
      mc.width = w
      mc.height = h
      paint.width = w
      paint.height = h
      const ictx = require2dContext(ic, 'initCanvasesTo:image')
      ictx.clearRect(0, 0, w, h)
      if (leftViewRef.current) {
        leftViewRef.current.width = w
        leftViewRef.current.height = h
      }
      if (rightViewRef.current) {
        rightViewRef.current.width = w
        rightViewRef.current.height = h
      }
      const mctx = require2dContext(mc, 'initCanvasesTo:mask')
      mctx.clearRect(0, 0, mc.width, mc.height)
      require2dContext(paint, 'initCanvasesTo:colorPaint').clearRect(0, 0, w, h)
    }

    /** Draws `img` scaled to the canvas's already-set (true source)
     * dimensions and records which quality tier now sits on the image
     * canvas. Never resizes the canvas itself — VistoeraTASK-00145's
     * whole safety story rests on the image/mask canvases' pixel
     * dimensions (and therefore every brush/mask/crop coordinate) never
     * changing after `initCanvasesTo` runs once per load. */
    function paintImageTier(img: HTMLImageElement, tier: 'placeholder' | 'full') {
      // VistoeraTASK-00610 — after confirmCrop the image canvas holds the
      // baked working image; never stretch the pristine original into it.
      if (workingImageLockedRef.current) return
      const ic = imageCanvasRef.current
      const ictx = require2dContext(ic, 'paintImageTier')
      ictx.clearRect(0, 0, ic.width, ic.height)
      // VistoeraTASK-00557 — `rotationRef.current` is always `0` for the
      // very first paint of a fresh load (reset above), so this is a
      // byte-for-byte no-op change for that case; it only diverges from
      // the old unconditional `drawImage` once a rotation has already
      // happened and the (placeholder or background-upgrade) full-
      // resolution leg paints a PRISTINE, unrotated `img` afterward —
      // §六's "left/right panes must never show different rotation
      // angles" applies just as much to a background quality upgrade as
      // it does to the user's own actions.
      drawPristineImageRotated(ictx, img, rotationRef.current, ic.width, ic.height)
      imageQualityTierRef.current = tier
    }

    // VistoeraTASK-00145 — the fast path only ever activates for the
    // Canvas Image Edit caller (`chainId` unset) when it already knows the
    // TRUE original pixel dimensions up front (`ImageAssetNode`'s
    // `masterMeta`, fetched well before the user opens the editor) AND has
    // a small, CORS-safe placeholder URL to paint first. Any caller that
    // omits either (every existing `ChainEditorModal` use, plus a
    // Canvas Image Edit open whose metadata fetch hasn't resolved yet)
    // falls through to the untouched, byte-for-byte original single-fetch
    // path below.
    const usePlaceholder =
      !chainId &&
      !!placeholderUrl &&
      typeof knownSourceWidth === 'number' &&
      typeof knownSourceHeight === 'number' &&
      knownSourceWidth > 0 &&
      knownSourceHeight > 0

    onFullResolutionReady?.(!usePlaceholder)

    if (!usePlaceholder) {
      ;(async () => {
        const img = await loadHtmlImage(proxyPixelUrl)
        if (cancelled || sourceKeyRef.current !== key) return
        patchEditorDiagnostics(diagnosticsRef, {
          phase: 'INITIALIZING_CANVAS',
          sourceNaturalWidth: img.naturalWidth || img.width,
          sourceNaturalHeight: img.naturalHeight || img.height,
        })
        const tCanvasInitStart = performance.now()
        initCanvasesTo(img.width, img.height)
        paintImageTier(img, 'full')
        const tCanvasInitEnd = performance.now()
        const ic = imageCanvasRef.current
        const mc = maskCanvasRef.current
        setImgSize({ w: img.width, h: img.height })
        onImageSize?.(img.width, img.height)
        patchEditorDiagnostics(diagnosticsRef, {
          imageCanvasWidth: ic.width,
          imageCanvasHeight: ic.height,
          maskCanvasWidth: mc.width,
          maskCanvasHeight: mc.height,
          leftViewCanvasWidth: leftViewRef.current?.width ?? 0,
          leftViewCanvasHeight: leftViewRef.current?.height ?? 0,
          rightViewCanvasWidth: rightViewRef.current?.width ?? 0,
          rightViewCanvasHeight: rightViewRef.current?.height ?? 0,
        })
        requestAnimationFrame(() => {
          if (sourceKeyRef.current !== key) return
          beginInitialFit()
          redrawLeft()
          setSourceReady(true)
          patchEditorDiagnostics(diagnosticsRef, { sourceReady: true })
          onSourceReady?.()
          onFullResolutionReady?.(true)
          applyTransform()
          const tEnd = performance.now()
          const timing = getLastPixelLoadTiming(proxyPixelUrl)
          if (chainId) {
            logEditorLoadTiming({
              asset_id: chainId,
              editor_type: 'transparent',
              source_kind: 'proxy',
              source_url_type: 'same-origin-pixels-proxy',
              source_width: img.width,
              source_height: img.height,
              source_bytes: timing?.bytes ?? null,
              metadata_ms: (typeof window !== 'undefined' ? window.__editorMetadataMs?.[chainId] : undefined) ?? null,
              image_ttfb_ms: timing?.ttfbMs ?? null,
              image_download_ms: timing?.downloadMs ?? null,
              decode_ms: timing?.decodeMs ?? null,
              canvas_init_ms: tCanvasInitEnd - tCanvasInitStart,
              first_draw_ms: tEnd - t0,
              total_ms: tEnd - t0,
              deduped_request: timing?.deduped ?? false,
            })
          } else {
            logEditorLoadTiming({
              asset_id: proxyPixelUrl.split('/assets/')[1]?.split('/')[0] ?? 'site_asset',
              editor_type: 'canvas_image_edit',
              source_kind: 'site_asset_master_pixels',
              source_url_type: 'same-origin-master-pixels',
              source_width: img.width,
              source_height: img.height,
              source_bytes: timing?.bytes ?? null,
              metadata_ms: null,
              image_ttfb_ms: timing?.ttfbMs ?? null,
              image_download_ms: timing?.downloadMs ?? null,
              decode_ms: timing?.decodeMs ?? null,
              canvas_init_ms: tCanvasInitEnd - tCanvasInitStart,
              first_draw_ms: tEnd - t0,
              total_ms: tEnd - t0,
              deduped_request: timing?.deduped ?? false,
            })
          }
        })
      })().catch((err: unknown) => {
        // Canvas Image Editor Ready-State P0 (VistoeraTASK-00070) — this
        // used to be `.catch(() => undefined)`: a failed canonical source
        // load (network blip, CORS misconfig, genuine 404/auth error —
        // anything) silently left `sourceReady` `false` forever with zero
        // signal to the caller. `mediaLoader.ts` already retries transient
        // failures internally, so anything that still reaches here is a
        // real, terminal failure — surface it explicitly instead of an
        // infinite spinner. A stale rejection from an already-superseded
        // (re)load (`sourceKeyRef.current !== key`, e.g. `proxyUrl` changed
        // or a retry was fired mid-flight) must never overwrite a newer
        // attempt's own in-flight/success state.
        if (cancelled || sourceKeyRef.current !== key) return
        const message = err instanceof Error ? err.message : String(err)
        patchEditorDiagnostics(diagnosticsRef, {
          phase: 'ERROR',
          lastSourceErrorClass: classifySourceErrorMessage(message),
        })
        onSourceError?.(message)
      })
      return () => {
        cancelled = true
      }
    }

    // ---------------------------------------------------------------
    // VistoeraTASK-00145 fast path — true dimensions already known, so
    // the canvases size themselves BEFORE any network byte arrives. Two
    // independent loads race: the small placeholder (usually wins, close
    // to the right pane's own `<img>` speed) and the true full-resolution
    // original (the pre-existing, slower, canonical fetch). Whichever
    // paints FIRST drives `sourceReady`/`onSourceReady` — the mask-init
    // effect (next `useEffect`) only depends on `sourceReady && imgSize.w`,
    // both already true by then, so the whole "原图加载中" -> red mask
    // chain now tracks the FAST leg, not the slow one.
    // ---------------------------------------------------------------
    const w = knownSourceWidth as number
    const h = knownSourceHeight as number
    const tCanvasInitStart = performance.now()
    initCanvasesTo(w, h)
    const tCanvasInitEnd = performance.now()
    patchEditorDiagnostics(diagnosticsRef, {
      phase: 'INITIALIZING_CANVAS',
      sourceNaturalWidth: w,
      sourceNaturalHeight: h,
      imageCanvasWidth: imageCanvasRef.current.width,
      imageCanvasHeight: imageCanvasRef.current.height,
      maskCanvasWidth: maskCanvasRef.current.width,
      maskCanvasHeight: maskCanvasRef.current.height,
      leftViewCanvasWidth: leftViewRef.current?.width ?? 0,
      leftViewCanvasHeight: leftViewRef.current?.height ?? 0,
      rightViewCanvasWidth: rightViewRef.current?.width ?? 0,
      rightViewCanvasHeight: rightViewRef.current?.height ?? 0,
    })

    let placeholderPainted = false
    let fullSettled = false

    function announceSourceReady() {
      setImgSize({ w, h })
      onImageSize?.(w, h)
      requestAnimationFrame(() => {
        if (sourceKeyRef.current !== key) return
        beginInitialFit()
        redrawLeft()
        setSourceReady(true)
        patchEditorDiagnostics(diagnosticsRef, { sourceReady: true })
        onSourceReady?.()
        applyTransform()
      })
    }

    function logFastPathTiming(url: string, sourceKind: string, sourceUrlType: string, tEnd: number) {
      const timing = getLastPixelLoadTiming(url)
      logEditorLoadTiming({
        asset_id: url.split('/assets/')[1]?.split('/')[0] ?? 'site_asset',
        editor_type: 'canvas_image_edit',
        source_kind: sourceKind,
        source_url_type: sourceUrlType,
        source_width: w,
        source_height: h,
        source_bytes: timing?.bytes ?? null,
        metadata_ms: null,
        image_ttfb_ms: timing?.ttfbMs ?? null,
        image_download_ms: timing?.downloadMs ?? null,
        decode_ms: timing?.decodeMs ?? null,
        canvas_init_ms: tCanvasInitEnd - tCanvasInitStart,
        first_draw_ms: tEnd - t0,
        total_ms: tEnd - t0,
        deduped_request: timing?.deduped ?? false,
      })
    }

    const fullPromise = loadHtmlImage(proxyPixelUrl)
    fullResPromiseRef.current = fullPromise

    // Fast leg — usually wins. A rejection here is silently absorbed: the
    // full-resolution leg below is still the canonical, authoritative load
    // and drives `sourceReady`/`onSourceError` on its own if this fails.
    ;(async () => {
      try {
        const phImg = await loadHtmlImage(placeholderUrl as string)
        if (cancelled || sourceKeyRef.current !== key || fullSettled) return
        paintImageTier(phImg, 'placeholder')
        placeholderPainted = true
        const tEnd = performance.now()
        announceSourceReady()
        logFastPathTiming(placeholderUrl as string, 'site_asset_display_preview', 'same-origin-web-pixels-placeholder', tEnd)
      } catch {
        /* non-fatal — see doc comment above */
      }
    })()

    // Canonical/authoritative leg.
    fullPromise
      .then((img) => {
        if (cancelled || sourceKeyRef.current !== key) return
        fullSettled = true
        const tEnd = performance.now()
        // VistoeraTASK-00430 — always safe to swap the background tier
        // now, even if the user already painted color-brush strokes:
        // those live on `colorPaintCanvasRef`, a layer this swap never
        // touches (see that ref's doc comment). Previously this was
        // gated on `!colorEditedRef.current` specifically to avoid
        // wiping strokes that were, at the time, baked directly into
        // this same canvas.
        paintImageTier(img, 'full')
        redrawLeft()
        onFullResolutionReady?.(true)
        if (!placeholderPainted) {
          announceSourceReady()
          logFastPathTiming(proxyPixelUrl, 'site_asset_master_pixels', 'same-origin-master-pixels', tEnd)
        } else {
          // Placeholder already drove `sourceReady` — this second,
          // authoritative paint is a silent quality upgrade only. Logged
          // as its own timing entry (VistoeraTASK-00145 forensics) rather
          // than folded into the first entry, so BEFORE/AFTER analysis can
          // see the true full-resolution leg's own network+decode cost.
          logFastPathTiming(proxyPixelUrl, 'site_asset_master_pixels', 'same-origin-master-pixels-background-upgrade', tEnd)
        }
      })
      .catch((err: unknown) => {
        if (cancelled || sourceKeyRef.current !== key) return
        fullSettled = true
        if (!placeholderPainted) {
          // Both legs failed (or the fast one hasn't resolved yet and
          // never will) — this is a real terminal failure, identical
          // semantics to the non-placeholder path's own catch block.
          const message = err instanceof Error ? err.message : String(err)
          patchEditorDiagnostics(diagnosticsRef, {
            phase: 'ERROR',
            lastSourceErrorClass: classifySourceErrorMessage(message),
          })
          onSourceError?.(message)
        } else {
          // The editor is already interactive at placeholder resolution;
          // never regress a live, editable surface back into an error/
          // spinner state over a background upgrade failing. Save/export
          // still safely falls back to the placeholder tier (see
          // `exportEditedImageBlob`'s own `fullResPromiseRef` await).
          onFullResolutionReady?.(false)
        }
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyUrl, chainId, sourceRetryNonce, placeholderUrl, knownSourceWidth, knownSourceHeight])

  useEffect(() => {
    if (!sourceReady || !imgSize.w) return
    if (!maskUrl) {
      if (!initialMaskFullyOpaque) return
      // No mask URL and no chain/auto-cutout step at all (Canvas Image
      // Edit) — synchronous, no network fetch needed.
      const key = `__fully-opaque__::${reloadToken}`
      maskKeyRef.current = key
      patchEditorDiagnostics(diagnosticsRef, { phase: 'INITIALIZING_MASK' })
      // VistoeraTASK-00412 — this call (and everything else in this
      // branch) runs SYNCHRONOUSLY inside this effect's own body, unlike
      // every other source/mask load path in this file (all `async`,
      // all already `.catch()`-guarded into `onSourceError`). A `null`
      // 2D context (see `require2dContext`'s doc comment) here used to
      // throw straight out of React's effect-commit phase, uncaught,
      // all the way to `ImageEditorErrorBoundary` — the exact reported
      // "imgedit-*" crash. Route it through the SAME graceful terminal-
      // failure signal every other path already uses instead.
      try {
        paintMaskFullyOpaque()
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        patchEditorDiagnostics(diagnosticsRef, {
          phase: 'ERROR',
          lastSourceErrorClass: classifySourceErrorMessage(message),
        })
        onSourceError?.(message)
        return
      }
      patchEditorDiagnostics(diagnosticsRef, { phase: 'INITIALIZING_HISTORY' })
      scheduleCommitBaselineHistory(false)
      requestAnimationFrame(() => {
        if (maskKeyRef.current !== key) return
        redraw()
        setPixelsReady(true)
        applyTransform()
        onSourceError?.(null)
        patchEditorDiagnostics(diagnosticsRef, { phase: 'READY', pixelsReady: true })
        devAssertReadyInvariant(imageCanvasRef.current, maskCanvasRef.current)
        onReady?.()
      })
      return
    }
    const key = `${maskUrl}::${reloadToken}`
    maskKeyRef.current = key
    let cancelled = false
    setPixelsReady(false)
    patchEditorDiagnostics(diagnosticsRef, { phase: 'INITIALIZING_MASK' })
    ;(async () => {
      const maskPixelUrl = chainId ? pixelsUrl(chainId, 'mask') : maskUrl
      const mimg = await loadHtmlImage(maskPixelUrl)
      if (cancelled || maskKeyRef.current !== key) return
      patchEditorDiagnostics(diagnosticsRef, { phase: 'INITIALIZING_HISTORY' })
      paintMaskFromImage(mimg)
      scheduleCommitBaselineHistory(false)
      requestAnimationFrame(() => {
        if (maskKeyRef.current !== key) return
        redraw()
        setPixelsReady(true)
        applyTransform()
        onSourceError?.(null)
        patchEditorDiagnostics(diagnosticsRef, { phase: 'READY', pixelsReady: true })
        devAssertReadyInvariant(imageCanvasRef.current, maskCanvasRef.current)
        onReady?.()
      })
    })().catch((err: unknown) => {
      // VistoeraTASK-00412 — this used to be `.catch(() => undefined)`:
      // a failed mask-image load/paint (including the same `null` 2D
      // context class this task root-caused in the sibling fully-opaque
      // branch above) was silently swallowed with zero signal, leaving
      // `pixelsReady` stuck `false` forever — an infinite spinner, never
      // a crash, but still never a real terminal state. Same graceful
      // `onSourceError` signal every other load path in this file uses.
      if (cancelled || maskKeyRef.current !== key) return
      const message = err instanceof Error ? err.message : String(err)
      patchEditorDiagnostics(diagnosticsRef, {
        phase: 'ERROR',
        lastSourceErrorClass: classifySourceErrorMessage(message),
      })
      onSourceError?.(message)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maskUrl, reloadToken, sourceReady, imgSize.w, initialMaskFullyOpaque, diagnosticsRef])

  useEffect(redraw, [redraw])

  useEffect(() => {
    const host = leftHostRef.current
    if (!host) return
    const ro = new ResizeObserver(() => {
      if (pendingFitRef.current) {
        if (tryFitWhenHostReady()) {
          const hostNow = leftHostRef.current
          const cw = hostNow?.clientWidth ?? 0
          const ch = hostNow?.clientHeight ?? 0
          const { w, h } = lastFitHostRef.current
          if (Math.abs(cw - w) <= 1 && Math.abs(ch - h) <= 1) {
            stableFitFramesRef.current += 1
            if (stableFitFramesRef.current >= 2) {
              pendingFitRef.current = false
            }
          } else {
            stableFitFramesRef.current = 0
          }
        }
      }
      applyTransform()
    })
    ro.observe(host)
    const onWinResize = () => {
      if (pendingFitRef.current) tryFitWhenHostReady()
      applyTransform()
    }
    window.addEventListener('resize', onWinResize)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', onWinResize)
    }
  }, [applyTransform, tryFitWhenHostReady])

  // Native non-passive wheel on BOTH image hosts — React 19's delegated
  // onWheel is passive at the root, so preventDefault is unreliable and
  // some overlay/handoff states dropped the synthetic path entirely.
  // Shared scale/offset still drives left+right via applyTransform.
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const host = e.currentTarget as HTMLDivElement
      // Zoom must never leak to the page behind the modal (wheel isolation
      // P0): cancel default scroll AND stop the event from bubbling to any
      // ancestor/document listener. This must run UNCONDITIONALLY, before
      // the "is there an image to zoom yet" check below — Preview Regression
      // Forensic P0 (2026-08-29) found the isolation calls previously sat
      // *after* the `imgSizeRef.current.w` early-return, so any wheel event
      // that arrived while the image was still loading (slow network, a
      // large real photo, or a transient backend hiccup) fell straight
      // through to the page's own scroll — reproducing the exact
      // "wheel/background list scrolls" regression on real (non-instant)
      // images even though this same isolation code was already present.
      e.preventDefault()
      e.stopPropagation()
      if (!imgSizeRef.current.w) return
      const rect = host.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      let dy = e.deltaY
      if (e.deltaMode === 1) dy *= 16
      else if (e.deltaMode === 2) dy *= 100
      dy = Math.max(-120, Math.min(120, dy))
      const WHEEL_SENSITIVITY = 0.0007
      let factor = Math.exp(-dy * WHEEL_SENSITIVITY)
      factor = Math.max(0.9, Math.min(1.1, factor))
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scaleRef.current * factor))
      const imgX = (mx - offsetRef.current.x) / scaleRef.current
      const imgY = (my - offsetRef.current.y) / scaleRef.current
      pendingFitRef.current = false
      setScale(newScale)
      setOffset({ x: mx - imgX * newScale, y: my - imgY * newScale })
    }
    const nodes = [leftHostRef.current, rightHostRef.current].filter(Boolean) as HTMLDivElement[]
    for (const n of nodes) n.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      for (const n of nodes) n.removeEventListener('wheel', handleWheel)
    }
  }, [imgSize.w, sourceReady, pixelsReady])

  const onProgressiveLeftLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (!img.naturalWidth || !img.naturalHeight) return
    // Size the stand-in like the canvas (natural px + shared transform),
    // never object-fit:contain full-bleed — that broke wheel sync.
    img.style.width = `${img.naturalWidth}px`
    img.style.height = `${img.naturalHeight}px`
    if (!sourceReady && !imgSizeRef.current.w) {
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight })
      onImageSize?.(img.naturalWidth, img.naturalHeight)
      beginInitialFit()
    } else {
      applyTransform()
    }
  }

  const onProgressiveRightLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (!img.naturalWidth || !img.naturalHeight) return
    img.style.width = `${img.naturalWidth}px`
    img.style.height = `${img.naturalHeight}px`
    applyTransform()
  }

  // ------------------------------------------------------------------
  // Painting primitives
  // ------------------------------------------------------------------

  /** VistoeraTASK-00080 — dispatches to the pre-existing selection-mask
   * dot (byte-for-byte unchanged, still the only path `ChainEditorModal`
   * ever exercises) or the new color-paint dot, based on `brushMode`.
   * Gated on `tool === 'brush'` specifically (not `'click'`) — color mode
   * is a Brush-only concept (P5); the Shell never sets `brushMode==='color'`
   * for the click/AI-point tool anyway, but this keeps the Core correct
   * on its own regardless of what any caller wires up. */
  function paintDot(x: number, y: number) {
    if (tool === 'brush' && brushMode === 'color') {
      paintColorDot(x, y)
      return
    }
    paintSelectionDot(x, y)
  }

  function paintSelectionDot(x: number, y: number) {
    const mc = maskCanvasRef.current
    const ctx = mc.getContext('2d')!
    const r = Math.max(1, brushSize / 2)
    const soft = Math.max(0.01, 1 - brushHardness)
    const grad = ctx.createRadialGradient(x, y, r * (1 - soft), x, y, r)
    if (polarity === 'add') {
      grad.addColorStop(0, `rgba(255,255,255,${brushOpacity})`)
      grad.addColorStop(1, 'rgba(255,255,255,0)')
      ctx.globalCompositeOperation = 'source-over'
    } else {
      grad.addColorStop(0, `rgba(0,0,0,${brushOpacity})`)
      grad.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.globalCompositeOperation = 'destination-out'
    }
    ctx.fillStyle = grad
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalCompositeOperation = 'source-over'
  }

  /** Local Color Brush P0 (VistoeraTASK-00080) — a genuinely different
   * operation from every other brush/shape tool in this file: it paints
   * real `brushColor` pixels onto `colorPaintCanvasRef` (a DEDICATED
   * layer, VistoeraTASK-00430 — see that ref's doc comment for why this
   * is no longer `imageCanvasRef` directly), instead of the mask canvas's
   * white/black "is this pixel selected" channel. `redrawLeft`/
   * `redrawRight`/`exportEditedImageBlob` all composite this layer on top
   * of whichever background tier is currently loaded via plain
   * `source-over`, so this function itself needs no awareness of
   * placeholder-vs-full at all. `polarity` has no meaning here (there's
   * no "subtract a color") — a color stroke always additively blends
   * `brushColor` over whatever was already painted via plain
   * `source-over`, exactly like `paintSelectionDot`'s `polarity==='add'`
   * branch already blends white. */
  function paintColorDot(x: number, y: number) {
    const r = Math.max(1, brushSize / 2)
    const soft = Math.max(0.01, 1 - brushHardness)

    // 1) Reveal the selection mask under the stroke — the EXACT same
    //    gradient/compositing math as `paintSelectionDot`'s `polarity===
    //    'add'` branch (P6: no new mask math), stamped through the
    //    identical (x, r, soft) shape as the color dot below so the two
    //    stay pixel-aligned. Painting real color somewhere always means
    //    "this pixel is visible" — regardless of any prior erase-to-
    //    transparent stroke under this exact spot (P15's "cover up this
    //    hair with white" only reads as fixed if the white is actually
    //    visible, not masked back out to transparent).
    const mc = maskCanvasRef.current
    const mctx = mc.getContext('2d')!
    const maskGrad = mctx.createRadialGradient(x, y, r * (1 - soft), x, y, r)
    maskGrad.addColorStop(0, `rgba(255,255,255,${brushOpacity})`)
    maskGrad.addColorStop(1, 'rgba(255,255,255,0)')
    mctx.globalCompositeOperation = 'source-over'
    mctx.fillStyle = maskGrad
    mctx.beginPath()
    mctx.arc(x, y, r, 0, Math.PI * 2)
    mctx.fill()

    // 2) Paint the actual color onto the dedicated paint layer (never
    //    `imageCanvasRef` any more — VistoeraTASK-00430) — this is the
    //    ONE place any tool in this file ever writes real RGB pixel color
    //    (every other tool only ever draws white/black into the mask
    //    canvas's alpha channel). `hexToRgb` accepts both `#rrggbb` and a
    //    stray `rgb(...)` CSS string (same tolerant parsing
    //    `backgroundPresets.ts` already uses for antd `ColorPicker`'s
    //    `onChange` payload — VistoeraTASK-00080 P7).
    const ic = colorPaintCanvasRef.current
    const ictx = ic.getContext('2d')!
    const [r8, g8, b8] = hexToRgb(brushColor)
    const colorGrad = ictx.createRadialGradient(x, y, r * (1 - soft), x, y, r)
    colorGrad.addColorStop(0, `rgba(${r8},${g8},${b8},${brushOpacity})`)
    colorGrad.addColorStop(1, `rgba(${r8},${g8},${b8},0)`)
    ictx.globalCompositeOperation = 'source-over'
    ictx.fillStyle = colorGrad
    ictx.beginPath()
    ictx.arc(x, y, r, 0, Math.PI * 2)
    ictx.fill()
  }

  function fillShape(path: (ctx: CanvasRenderingContext2D) => void) {
    const mc = maskCanvasRef.current
    const ctx = mc.getContext('2d')!
    ctx.globalCompositeOperation = polarity === 'add' ? 'source-over' : 'destination-out'
    ctx.fillStyle = polarity === 'add' ? '#ffffff' : '#000000'
    path(ctx)
    ctx.globalCompositeOperation = 'source-over'
  }

  const invertSelection = useCallback(() => {
    const mc = maskCanvasRef.current
    // Editor Stability P0 (VistoeraTASK-00103) — see `pushHistory`'s doc
    // comment. This is the single MOST directly reachable crash path found
    // during forensics: the "反选" toolbar button is never disabled while
    // the source is still loading, so one impatient click before ready threw
    // `IndexSizeError` straight out of a Button's onClick with zero warning.
    if (!canvasPixelOperationAllowed(mc.width, mc.height)) return
    const ctx = mc.getContext('2d')!
    const data = ctx.getImageData(0, 0, mc.width, mc.height)
    for (let i = 0; i < data.data.length; i += 4) {
      data.data[i + 3] = 255 - data.data[i + 3]
      data.data[i] = 255
      data.data[i + 1] = 255
      data.data[i + 2] = 255
    }
    ctx.putImageData(data, 0, 0)
    pushHistory()
    onDirtyChange?.(true)
    redraw()
  }, [pushHistory, onDirtyChange, redraw])

  /** VistoeraTASK-00557 — resizes/physically rotates `imageCanvasRef`/
   * `maskCanvasRef`/`colorPaintCanvasRef` (plus the output-only
   * `leftViewRef`/`rightViewRef`) FROM whatever `rotationRef.current`
   * currently is TO `targetRotation`, then syncs `imgSizeRef`/`imgSize`/
   * `onImageSize`. A true no-op (nothing resized, no re-render) when
   * `targetRotation` already matches — the overwhelmingly common case
   * for every undo/redo step that doesn't cross a rotation boundary.
   *
   * Always rotates ALL THREE backing canvases' actual content (never
   * just resizes), including `mask`/`paint` even though `undo`/`redo`
   * immediately overwrite them via `putImageData`/`restoreImageStateFor`
   * right after calling this — correct either way, and keeping one
   * single code path (instead of a second "resize-only" variant just
   * for that call site) is the simpler, lower-risk option for §十三's
   * "only the minimal modification strictly necessary". `imageCanvasRef`
   * specifically is NEVER snapshotted in history at all (VistoeraTASK-
   * 00430 — a silently-swappable background tier, see its own doc
   * comment), so rotating its real content here is the ONLY way it ever
   * stays visually correct across an undo/redo that crosses a rotation
   * boundary. */
  const applyRotationState = useCallback(
    (targetRotation: number) => {
      const delta = (((targetRotation - rotationRef.current) / 90) % 4 + 4) % 4
      if (delta === 0) return
      rotateCanvasContentBy90(imageCanvasRef.current, delta)
      rotateCanvasContentBy90(maskCanvasRef.current, delta)
      rotateCanvasContentBy90(colorPaintCanvasRef.current, delta)
      const w = imageCanvasRef.current.width
      const h = imageCanvasRef.current.height
      if (leftViewRef.current) {
        leftViewRef.current.width = w
        leftViewRef.current.height = h
      }
      if (rightViewRef.current) {
        rightViewRef.current.width = w
        rightViewRef.current.height = h
      }
      rotationRef.current = ((targetRotation % 360) + 360) % 360
      imgSizeRef.current = { w, h }
      setImgSize({ w, h })
      onImageSize?.(w, h)
    },
    [onImageSize],
  )

  /** VistoeraTASK-00557 §三/§四 — the "旋转" toolbar button's action: one
   * instant, exactly-90°-clockwise step, immediately entering the SAME
   * undo/redo stack every other edit uses (§八). Deliberately NOT a
   * persistent `EditorTool` mode (nothing to "select" — mirrors the
   * pre-existing "反选"/invertSelection instant-action precedent). Never
   * touches the network / re-fetches the original (§十二) — purely a
   * synchronous, in-memory rotation of the already-loaded canvases. */
  const rotateClockwise90 = useCallback(() => {
    const ic = imageCanvasRef.current
    if (!canvasPixelOperationAllowed(ic.width, ic.height)) return
    const prevW = ic.width
    const prevH = ic.height
    applyRotationState((rotationRef.current + 90) % 360)
    // §七 — any in-progress crop/selection interaction is safely cleared
    // (explicitly permitted) rather than mathematically re-projected
    // through the rotation; a crop drawn AFTER this point starts fresh
    // in the already-rotated, correct coordinate space.
    setCropRect(null)
    cropDrag.current = null
    drawing.current = false
    // VistoeraTASK-00598 — rotate annotation bboxes with the image so
    // stroke/fill marks stay aligned (unlike crop, which is cleared).
    if (annotationsRef.current.length > 0) {
      setAnnotations(
        annotationsRef.current.map((a) => rotateAnnotationClockwise90(a, prevW, prevH)),
      )
    }
    pendingShapeRef.current = null
    pushHistory(true)
    onDirtyChange?.(true)
    beginInitialFit()
    redraw()
  }, [applyRotationState, pushHistory, onDirtyChange, beginInitialFit, redraw])

  /** Resize every backing + view canvas to ``w``×``h``, optionally
   * scaling existing pixel content (default) or leaving them blank for
   * a subsequent `putImageData` / `drawImage` restore. */
  const setBackingCanvasSize = useCallback((w: number, h: number, scaleContent: boolean) => {
    const scaleOne = (canvas: HTMLCanvasElement) => {
      if (canvas.width === w && canvas.height === h) return
      if (!scaleContent || canvas.width < 1 || canvas.height < 1) {
        canvas.width = w
        canvas.height = h
        return
      }
      const tmp = document.createElement('canvas')
      tmp.width = canvas.width
      tmp.height = canvas.height
      tmp.getContext('2d')!.drawImage(canvas, 0, 0)
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')!
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(tmp, 0, 0, w, h)
    }
    scaleOne(imageCanvasRef.current)
    scaleOne(maskCanvasRef.current)
    scaleOne(colorPaintCanvasRef.current)
    if (leftViewRef.current) {
      leftViewRef.current.width = w
      leftViewRef.current.height = h
    }
    if (rightViewRef.current) {
      rightViewRef.current.width = w
      rightViewRef.current.height = h
    }
    imgSizeRef.current = { w, h }
    setImgSize({ w, h })
    onImageSize?.(w, h)
  }, [onImageSize])

  /** Restore a history entry's pixel dimensions + optional background
   * snapshot before `putImageData` of mask/paint. Handles both rotation
   * boundaries (via `applyRotationState`) and resize boundaries. */
  const restoreEntryGeometry = useCallback(
    (entry: HistoryEntry) => {
      applyRotationState(entry.rotation)
      const targetW = entry.mask.width
      const targetH = entry.mask.height
      const ic = imageCanvasRef.current
      if (ic.width !== targetW || ic.height !== targetH) {
        if (entry.background) {
          setBackingCanvasSize(targetW, targetH, false)
          ic.getContext('2d')!.putImageData(entry.background, 0, 0)
        } else {
          // No background snapshot (pre-resize ordinary entry that somehow
          // crossed a size boundary) — scale whatever is currently painted.
          setBackingCanvasSize(targetW, targetH, true)
        }
      } else if (entry.background) {
        ic.getContext('2d')!.putImageData(entry.background, 0, 0)
      }
    },
    [applyRotationState, setBackingCanvasSize],
  )

  const resizeTo = useCallback(
    (width: number, height: number) => {
      const ic = imageCanvasRef.current
      if (!canvasPixelOperationAllowed(ic.width, ic.height)) return false
      const w = Math.round(width)
      const h = Math.round(height)
      if (!Number.isInteger(w) || !Number.isInteger(h) || w < 1 || h < 1) return false
      if (w > ic.width || h > ic.height) return false
      if (w === ic.width && h === ic.height) return false
      ensureBackgroundOnCurrentHistory()
      const sx = w / ic.width
      const sy = h / ic.height
      setBackingCanvasSize(w, h, true)
      setCropRect(null)
      cropDrag.current = null
      drawing.current = false
      if (annotationsRef.current.length > 0) {
        setAnnotations(annotationsRef.current.map((a) => scaleAnnotation(a, sx, sy)))
      }
      pendingShapeRef.current = null
      pushHistoryWithBackground()
      onDirtyChange?.(true)
      beginInitialFit()
      redraw()
      return true
    },
    [
      ensureBackgroundOnCurrentHistory,
      setBackingCanvasSize,
      pushHistoryWithBackground,
      onDirtyChange,
      beginInitialFit,
      redraw,
    ],
  )

  const applyResizedBitmap = useCallback(
    (source: CanvasImageSource, width: number, height: number) => {
      const ic = imageCanvasRef.current
      if (!canvasPixelOperationAllowed(ic.width, ic.height)) return false
      const w = Math.round(width)
      const h = Math.round(height)
      if (!Number.isInteger(w) || !Number.isInteger(h) || w < 1 || h < 1) return false
      if (w > ic.width || h > ic.height) return false
      ensureBackgroundOnCurrentHistory()
      // Scale mask/paint with content; replace image canvas from source.
      const paint = colorPaintCanvasRef.current
      const mc = maskCanvasRef.current
      const scaleLayer = (canvas: HTMLCanvasElement) => {
        if (canvas.width === w && canvas.height === h) return
        const tmp = document.createElement('canvas')
        tmp.width = canvas.width
        tmp.height = canvas.height
        tmp.getContext('2d')!.drawImage(canvas, 0, 0)
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')!
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(tmp, 0, 0, w, h)
      }
      scaleLayer(mc)
      scaleLayer(paint)
      ic.width = w
      ic.height = h
      const ictx = ic.getContext('2d')!
      ictx.clearRect(0, 0, w, h)
      ictx.drawImage(source, 0, 0, w, h)
      if (leftViewRef.current) {
        leftViewRef.current.width = w
        leftViewRef.current.height = h
      }
      if (rightViewRef.current) {
        rightViewRef.current.width = w
        rightViewRef.current.height = h
      }
      imgSizeRef.current = { w, h }
      setImgSize({ w, h })
      onImageSize?.(w, h)
      setCropRect(null)
      cropDrag.current = null
      drawing.current = false
      // File-size recompress replaces the bitmap; drop annotations that
      // would otherwise sit at stale source coordinates.
      setAnnotations([])
      pendingShapeRef.current = null
      pushHistoryWithBackground()
      onDirtyChange?.(true)
      beginInitialFit()
      redraw()
      return true
    },
    [ensureBackgroundOnCurrentHistory, pushHistoryWithBackground, onDirtyChange, beginInitialFit, redraw, onImageSize],
  )

  const applyShapeDrag = (p: { x: number; y: number }) => {
    const drag = shapeDragRef.current
    if (!drag) return
    drag.lastImg = p
    if (drag.mode === 'move' && drag.annId && drag.origAnn) {
      const dx = p.x - drag.startImg.x
      const dy = p.y - drag.startImg.y
      // Mutate canonical list in place for live preview (left+right redraw
      // both read annotationsRef). History is pushed once on pointerup.
      annotationsRef.current = annotationsRef.current.map((a) =>
        a.id === drag.annId ? translateShapeAnnotation(drag.origAnn!, dx, dy) : a,
      )
      return
    }
    const type = shapeBrushTypeRef.current
    const geom = geomFromShapeDrag(type, drag.startImg, p, shiftHeldRef.current)
    pendingShapeRef.current = {
      id: 'pending',
      type,
      x: geom.x,
      y: geom.y,
      w: geom.w,
      h: geom.h,
      strokeColor: shapeStrokeColorRef.current,
      strokeWidth: Math.max(0.5, shapeStrokeWidthRef.current),
      fillEnabled: shapeFillEnabledRef.current,
      fillColor: shapeFillColorRef.current,
      fillOpacity: Math.min(1, Math.max(0, shapeFillOpacityRef.current)),
    }
  }

  const restoreShapeMoveIfNeeded = () => {
    const drag = shapeDragRef.current
    if (drag?.mode === 'move' && drag.annId && drag.origAnn) {
      annotationsRef.current = annotationsRef.current.map((a) =>
        a.id === drag.annId ? { ...drag.origAnn! } : a,
      )
    }
  }

  const buildWorkingCompositeCanvas = useCallback(() => {
    const ic = imageCanvasRef.current
    const mc = maskCanvasRef.current
    if (!canvasPixelOperationAllowed(ic.width, ic.height)) return null
    const out = document.createElement('canvas')
    out.width = ic.width
    out.height = ic.height
    const ctx = out.getContext('2d')!
    ctx.drawImage(ic, 0, 0)
    ctx.drawImage(colorPaintCanvasRef.current, 0, 0)
    ctx.globalCompositeOperation = 'destination-in'
    ctx.drawImage(mc, 0, 0)
    ctx.globalCompositeOperation = 'source-over'
    drawShapeAnnotations(ctx, annotationsRef.current)
    return out
  }, [])

  const clearPendingEdits = useCallback(() => {
    // VistoeraTASK-00610 — Reset always restores the session ORIGINAL
    // (pre-confirmCrop), never the post-crop working baseline.
    const origin = sessionOriginalRef.current ?? baselineData.current
    if (!origin) return
    workingImageLockedRef.current = false
    baselineData.current = origin
    // VistoeraTASK-00557 — "重置" conceptually discards every edit back
    // to the original, unrotated source; without this, a rotated canvas
    // would stay rotated while mask/paint snapped back to the baseline's
    // (unrotated) dimensions — an immediate, broken size mismatch.
    // VistoeraTASK-00590 — also restores baseline pixel dimensions after
    // a resize, re-painting the full-resolution original when available.
    restoreEntryGeometry(origin)
    // If baseline has no background snapshot (common — load path never
    // captures one), re-paint from the still-cached full-resolution
    // original so Reset after a resize does not leave a soft-scaled
    // remnant of the shrunk image.
    if (!origin.background && fullResPromiseRef.current) {
      const pending = fullResPromiseRef.current
      void pending.then((fullImg) => {
        if (workingImageLockedRef.current) return
        if (!baselineData.current) return
        const ic0 = imageCanvasRef.current
        const bw = baselineData.current.mask.width
        const bh = baselineData.current.mask.height
        if (ic0.width !== bw || ic0.height !== bh) return
        const ictx0 = ic0.getContext('2d')!
        ictx0.clearRect(0, 0, bw, bh)
        drawPristineImageRotated(ictx0, fullImg, rotationRef.current, bw, bh)
        imageQualityTierRef.current = 'full'
        redraw()
      })
    }
    const mc = maskCanvasRef.current
    const ctx = mc.getContext('2d')!
    ctx.putImageData(origin.mask, 0, 0)
    // VistoeraTASK-00080/VistoeraTASK-00430 — "重置" must also undo any
    // color-brush strokes, not just selection-mask edits. The paint layer
    // always starts fully blank/transparent (no caller ever opens the
    // editor with pre-existing color-brush strokes), so clearing it is
    // always correct regardless of whether a baseline snapshot was
    // actually captured (`commitBaselineHistory`'s `captureImageSnapshot`
    // is `false` at every current call site) — cheaper and more robust
    // than restoring a snapshot that may not exist.
    const paint = colorPaintCanvasRef.current
    if (origin.image) {
      paint.getContext('2d')!.putImageData(origin.image, 0, 0)
    } else {
      paint.getContext('2d')!.clearRect(0, 0, paint.width, paint.height)
    }
    setCropRect(origin.crop)
    setAnnotations(cloneAnnotations(origin.annotations))
    setSelectedShapeId(null)
    setShapeCtxMenu(null)
    pendingShapeRef.current = null
    history.current = [origin]
    historyIdx.current = 0
    onDirtyChange?.(false)
    emitHistoryChange()
    beginInitialFit()
    redraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDirtyChange, redraw, restoreEntryGeometry, beginInitialFit, emitHistoryChange])

  const undo = useCallback(() => {
    if (historyIdx.current <= 0) return
    historyIdx.current -= 1
    const entry = history.current[historyIdx.current]
    // VistoeraTASK-00557 / VistoeraTASK-00590 — restore rotation AND
    // pixel dimensions (with background snapshot when present) before
    // putImageData.
    restoreEntryGeometry(entry)
    const mc = maskCanvasRef.current
    mc.getContext('2d')!.putImageData(entry.mask, 0, 0)
    restoreImageStateFor(historyIdx.current)
    setCropRect(entry.crop)
    setAnnotations(cloneAnnotations(entry.annotations))
    onDirtyChange?.(historyIdx.current !== 0)
    emitHistoryChange()
    beginInitialFit()
    redraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDirtyChange, redraw, restoreImageStateFor, restoreEntryGeometry, beginInitialFit, emitHistoryChange])

  const redo = useCallback(() => {
    if (historyIdx.current >= history.current.length - 1) return
    historyIdx.current += 1
    const entry = history.current[historyIdx.current]
    restoreEntryGeometry(entry)
    const mc = maskCanvasRef.current
    mc.getContext('2d')!.putImageData(entry.mask, 0, 0)
    restoreImageStateFor(historyIdx.current)
    setCropRect(entry.crop)
    setAnnotations(cloneAnnotations(entry.annotations))
    onDirtyChange?.(true)
    emitHistoryChange()
    beginInitialFit()
    redraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDirtyChange, redraw, restoreImageStateFor, restoreEntryGeometry, beginInitialFit, emitHistoryChange])

  /** VistoeraTASK-00610 — bake crop into working image; rebase undo floor. */
  const confirmCrop = useCallback(async () => {
    const crop = cropRef.current
    if (!crop) return false
    const ic = imageCanvasRef.current
    const mc = maskCanvasRef.current
    if (!canvasPixelOperationAllowed(ic.width, ic.height)) return false

    // Prefer full-res pixels before baking when still on placeholder tier.
    if (!workingImageLockedRef.current && imageQualityTierRef.current !== 'full' && fullResPromiseRef.current) {
      try {
        const pendingFullRes = fullResPromiseRef.current
        const fullImg = await Promise.race([
          pendingFullRes,
          new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('full-resolution original wait timed out')), EXPORT_FULL_RESOLUTION_WAIT_TIMEOUT_MS)
          }),
        ])
        if (!workingImageLockedRef.current) {
          const ictx0 = ic.getContext('2d')!
          ictx0.clearRect(0, 0, ic.width, ic.height)
          drawPristineImageRotated(ictx0, fullImg, rotationRef.current, ic.width, ic.height)
          imageQualityTierRef.current = 'full'
        }
      } catch {
        /* bake whatever is currently painted */
      }
    }

    const cropNorm = normalizeCropRect(crop)
    const sized = cropOutputSize(cropNorm, ic.width, ic.height)
    const finite = (v: number, fallback: number) => (Number.isFinite(v) ? v : fallback)
    const sx = Math.min(Math.max(0, Math.round(finite(sized.sx, 0))), Math.max(0, ic.width - 1))
    const sy = Math.min(Math.max(0, Math.round(finite(sized.sy, 0))), Math.max(0, ic.height - 1))
    const sw = Math.min(Math.max(1, Math.round(finite(sized.sw, ic.width))), ic.width - sx)
    const sh = Math.min(Math.max(1, Math.round(finite(sized.sh, ic.height))), ic.height - sy)

    const composed = document.createElement('canvas')
    composed.width = ic.width
    composed.height = ic.height
    const cctx = composed.getContext('2d')!
    cctx.drawImage(ic, 0, 0)
    cctx.drawImage(colorPaintCanvasRef.current, 0, 0)
    cctx.globalCompositeOperation = 'destination-in'
    cctx.drawImage(mc, 0, 0)
    cctx.globalCompositeOperation = 'source-over'
    drawShapeAnnotations(cctx, annotationsRef.current)
    cctx.save()
    applyCropShapeComposite(cctx, cropNorm)
    cctx.restore()

    const out = document.createElement('canvas')
    out.width = sw
    out.height = sh
    out.getContext('2d')!.drawImage(composed, sx, sy, sw, sh, 0, 0, sw, sh)

    // Drop prior undo stack; sessionOriginalRef keeps the Reset snapshot.
    history.current = []
    historyIdx.current = -1

    setBackingCanvasSize(sw, sh, false)
    const ictx = ic.getContext('2d')!
    ictx.clearRect(0, 0, sw, sh)
    ictx.drawImage(out, 0, 0)

    const paint = colorPaintCanvasRef.current
    paint.getContext('2d')!.clearRect(0, 0, sw, sh)
    const mctx = mc.getContext('2d')!
    mctx.globalCompositeOperation = 'source-over'
    mctx.clearRect(0, 0, sw, sh)
    mctx.fillStyle = 'rgba(255,255,255,1)'
    mctx.fillRect(0, 0, sw, sh)

    // Crop result is already rotation-baked into pixels.
    rotationRef.current = 0
    setCropRect(null)
    cropDrag.current = null
    drawing.current = false
    setAnnotations([])
    pendingShapeRef.current = null
    setSelectedShapeId(null)
    setShapeCtxMenu(null)

    const background = ictx.getImageData(0, 0, sw, sh)
    const maskData = mctx.getImageData(0, 0, sw, sh)
    const newBaseline: HistoryEntry = {
      mask: maskData,
      image: null,
      background,
      crop: null,
      rotation: 0,
      annotations: [],
    }
    baselineData.current = newBaseline
    history.current = [newBaseline]
    historyIdx.current = 0
    baselineHistoryReadyRef.current = true
    workingImageLockedRef.current = true
    imageQualityTierRef.current = 'full'
    onDirtyChange?.(true)
    emitHistoryChange()
    beginInitialFit()
    redraw()
    return true
  }, [
    setBackingCanvasSize,
    onDirtyChange,
    emitHistoryChange,
    beginInitialFit,
    redraw,
  ])

  useImperativeHandle(
    ref,
    () => ({
      exportMaskBlob: async () => {
        const mc = maskCanvasRef.current
        // Editor Stability P0 (VistoeraTASK-00103) — a controlled rejection
        // (caught by every existing caller already) instead of letting
        // `getImageData(0, 0, 0, 0)` throw a raw `IndexSizeError` below.
        if (!canvasPixelOperationAllowed(mc.width, mc.height)) throw new Error(t('cutoutEditor.exportMaskFailed'))
        const out = document.createElement('canvas')
        out.width = mc.width
        out.height = mc.height
        const octx = out.getContext('2d')!
        const src = mc.getContext('2d')!.getImageData(0, 0, mc.width, mc.height)
        const dst = octx.createImageData(mc.width, mc.height)
        for (let i = 0; i < src.data.length; i += 4) {
          const a = src.data[i + 3]
          dst.data[i] = a
          dst.data[i + 1] = a
          dst.data[i + 2] = a
          dst.data[i + 3] = 255
        }
        octx.putImageData(dst, 0, 0)
        return new Promise<Blob>((resolve, reject) => {
          out.toBlob((b) => (b ? resolve(b) : reject(new Error(t('cutoutEditor.exportMaskFailed')))), 'image/png')
        })
      },
      exportTransparentPngBlob: async () => {
        const ic = imageCanvasRef.current
        const mc = maskCanvasRef.current
        if (!canvasPixelOperationAllowed(ic.width, ic.height)) {
          throw new Error(t('cutoutEditor.exportMaskFailed'))
        }
        if (ic.width !== mc.width || ic.height !== mc.height) {
          throw new Error(t('cutoutEditor.exportMaskFailed'))
        }
        const out = document.createElement('canvas')
        out.width = ic.width
        out.height = ic.height
        const octx = out.getContext('2d')!
        octx.drawImage(ic, 0, 0)
        const rgba = octx.getImageData(0, 0, out.width, out.height)
        const mask = mc.getContext('2d')!.getImageData(0, 0, mc.width, mc.height)
        for (let i = 0; i < rgba.data.length; i += 4) {
          rgba.data[i + 3] = mask.data[i + 3]
        }
        octx.putImageData(rgba, 0, 0)
        return new Promise<Blob>((resolve, reject) => {
          out.toBlob((b) => (b ? resolve(b) : reject(new Error(t('cutoutEditor.exportMaskFailed')))), 'image/png')
        })
      },
      exportEditedImageBlob: async (onStage) => {
        patchEditorDiagnostics(diagnosticsRef, { phase: 'SAVING' })
        // VistoeraTASK-00504 — see `exportInFlightRef`'s doc comment: held
        // for the entire export (including the full-resolution wait
        // below), reset in `finally` so it clears on both success and
        // every failure/throw path, never leaving new edits permanently
        // blocked. `cropAtExportStart` is a SNAPSHOT of the committed
        // crop rect taken BEFORE that wait — the actual crop/export math
        // further down uses this snapshot, not a fresh `cropRef.current`
        // read, so even if `onLeftPointerDown`'s new guard were somehow
        // bypassed, the crop geometry baked into THIS export can never
        // drift to whatever the user is dragging *right now*.
        exportInFlightRef.current = true
        const cropAtExportStart = cropRef.current
        try {
        // VistoeraTASK-00145/VistoeraTASK-00430 — if the image canvas
        // still only holds the fast placeholder tier (now a MUCH more
        // likely case than before this task — color-brush strokes no
        // longer wait for the full-resolution original at all, so a user
        // can realistically click "另存编辑图片" while a large original is
        // still downloading in the background), wait for the same
        // in-flight promise the source-load effect is already awaiting —
        // cheap even if it already settled — and paint it before
        // composing the export. This is the guarantee behind
        // `FINAL_SAVE_REQUIRES_BROWSER_ORIGINAL`/
        // `FINAL_OUTPUT_RESOLUTION_PRESERVED`: the working-resolution
        // interactive speed optimization can never leak into a
        // permanently downgraded save — the Shell surfaces a "正在准备
        // 原图用于最终输出" message for this specific wait (see
        // `CanvasImageEditorOverlay.onSaveAsNew`). No longer gated on a
        // "has the user already painted color?" check — safe
        // unconditionally now that strokes live on `colorPaintCanvasRef`,
        // untouched by this swap.
        // VistoeraTASK-00610 — after confirmCrop the working canvas IS the
        // authoritative image; never re-paint the pristine original over it.
        if (
          !workingImageLockedRef.current &&
          imageQualityTierRef.current !== 'full' &&
          fullResPromiseRef.current
        ) {
          try {
            // VistoeraTASK-00432 — bounded wait, not an unconditional
            // `await`. Forensically confirmed (TASK-00427, re-confirmed
            // this task) that on Preview the full-resolution leg goes
            // through `GET .../variants/original/pixels`, which is
            // *intentionally* pinned to `AssetSecurityClass.DYNAMIC_API`
            // (same-origin Render byte relay, never a presigned R2
            // redirect — see `app/api/asset_delivery.py`'s doc comment
            // and `get_variant_pixels`'s own, both re-verified live this
            // task) because the R2 bucket genuinely has no CORS policy
            // for the app origin yet (re-confirmed live this task: a
            // real presigned-GET OPTIONS preflight from the app origin
            // returns 403 with no `Access-Control-Allow-Origin` at all) —
            // `fetch(..., {credentials:'include'})` would otherwise fail
            // outright. For a large original this relay can take on the
            // order of minutes, during which the OLD code here left
            // "另存编辑图片" (and the whole editor, per
            // `CanvasImageEditorOverlay.onSaveAsNew`'s synchronous
            // await chain) completely stuck with no way to close. A
            // `Promise.race` against a generous-but-finite timeout keeps
            // `FINAL_OUTPUT_RESOLUTION_PRESERVED` intact in the
            // overwhelmingly common case (full-res already resolved, or
            // resolves well inside the window) while guaranteeing the
            // save can never again hang for literal minutes: past the
            // timeout, export proceeds at whatever tier is already
            // painted (placeholder), exactly like the genuine-failure
            // catch branch below already did. This is a deliberate
            // stopgap, not the complete fix — the complete fix is
            // migrating `/pixels` off the Render relay once the R2
            // bucket CORS policy is updated for the app origin (tracked
            // separately; out of scope for a blind client-side change).
            const pendingFullRes = fullResPromiseRef.current
            const fullImg = await Promise.race([
              pendingFullRes,
              new Promise<never>((_, reject) => {
                setTimeout(() => reject(new Error('full-resolution original wait timed out')), EXPORT_FULL_RESOLUTION_WAIT_TIMEOUT_MS)
              }),
            ])
            const ic0 = imageCanvasRef.current
            const ictx0 = ic0.getContext('2d')!
            ictx0.clearRect(0, 0, ic0.width, ic0.height)
            // VistoeraTASK-00557 — `fullImg` is always the PRISTINE,
            // never-rotated original (the browser `Image` decoded
            // straight from `proxyPixelUrl`'s bytes); if the user has
            // rotated since this editor opened, `ic0` is now sized to
            // the ROTATED (possibly width/height-swapped) dimensions, so
            // a plain stretch-fit `drawImage` here would silently
            // distort it into the wrong shape (§五's explicit warning).
            // Same shared helper `paintImageTier` already uses for the
            // identical background-upgrade case — one geometry, not two.
            drawPristineImageRotated(ictx0, fullImg, rotationRef.current, ic0.width, ic0.height)
            imageQualityTierRef.current = 'full'
            redrawLeft()
            redrawRight()
          } catch (err) {
            // Full-resolution leg genuinely failed OR timed out — export
            // proceeds with whatever is already on the canvas (placeholder
            // tier) rather than blocking "另存为" forever (or for minutes)
            // on a slow/failed load; this mirrors `onSourceError`'s own
            // "no infinite spinner on a real terminal failure" rule. The
            // background full-resolution fetch itself is NOT cancelled —
            // if it later resolves, the normal source-load effect's own
            // `.then()` still silently upgrades the on-screen canvas for
            // any *subsequent* save/export.
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.warn('[DualPaneCutoutEditor] exportEditedImageBlob proceeding at placeholder tier', err)
            }
          }
        }
        // VistoeraTASK-00441 — T1_FULL_RESOLUTION_READY/T2_EXPORT_START
        // boundary: the full-resolution wait above (if any) has now
        // settled one way or another and the actual composite/encode
        // work below is about to start. Fired even when the `if` above
        // was skipped entirely (already full-tier) — that is itself a
        // real, reportable `FULL_RESOLUTION_WAIT_MS≈0` data point.
        onStage?.('full_resolution_ready')
        const ic = imageCanvasRef.current
        const mc = maskCanvasRef.current
        // Editor Stability P0 (VistoeraTASK-00103) — "另存编辑图片" clicked
        // before the source finished loading must fail cleanly (the Shell's
        // `onSaveAsNew` already wraps this call in try/catch and shows
        // `imageEditor.saveFailed`), never produce a nonsensical empty PNG.
        if (!canvasPixelOperationAllowed(ic.width, ic.height)) throw new Error(t('cutoutEditor.exportMaskFailed'))
        // VistoeraTASK-00504 — reads the pre-await SNAPSHOT (see top of
        // this function), not a fresh `cropRef.current`, so a crop drag
        // the user starts/continues during the full-resolution wait
        // above can never leak into THIS export's geometry.
        const crop = cropAtExportStart
        // VistoeraTASK-00504 — last-line-of-defense clamp against the
        // REAL, final `ic.width`/`ic.height` (never `imgSizeRef`, which
        // is a separate value that is only EXPECTED to always match —
        // see `clampCropRect`'s doc comment). `Math.max`/`Math.min`
        // silently propagate a non-finite input rather than falling
        // back to the other operand, so a plain `Math.max(0, crop.x)`
        // alone would NOT actually catch a NaN/Infinity rect — finite-
        // guard first, then clamp into `[0, ic.width]`/`[0, ic.height]`
        // so `sx+sw`/`sy+sh` can never exceed the source canvas (section
        // 四.6/四.9 of this task's own audit checklist) and `sw`/`sh` can
        // never collapse to `0` (which would otherwise silently zero out
        // `out.width`/`out.height` per the HTML canvas spec).
        const finite = (v: number, fallback: number) => (Number.isFinite(v) ? v : fallback)
        // VistoeraTASK-00597 §七 — invert keeps full image size; normal
        // crop uses the shape bounding box. Legacy callers without
        // shape/invert still behave as a free-rect bbox extract.
        const cropNorm = crop ? normalizeCropRect(crop) : null
        const sized = cropNorm
          ? cropOutputSize(cropNorm, ic.width, ic.height)
          : { sx: 0, sy: 0, sw: ic.width, sh: ic.height }
        const sx = Math.min(Math.max(0, Math.round(finite(sized.sx, 0))), Math.max(0, ic.width - 1))
        const sy = Math.min(Math.max(0, Math.round(finite(sized.sy, 0))), Math.max(0, ic.height - 1))
        const sw = Math.min(Math.max(1, Math.round(finite(sized.sw, ic.width))), ic.width - sx)
        const sh = Math.min(Math.max(1, Math.round(finite(sized.sh, ic.height))), ic.height - sy)
        // Composite image ∩ mask at the FULL loaded resolution first —
        // identical math to `redrawRight` (minus the bgMode fill, which
        // is a preview-only affordance, never baked into the export),
        // then extract the crop rect (or the whole frame). Both steps
        // operate purely on canvas pixel coordinates, which are already
        // 1:1 with the source image's natural size — never screen/CSS
        // pixels (VISTOERA_CANVAS_IMAGE_EDIT_CROP_P0 §20).
        const composed = document.createElement('canvas')
        composed.width = ic.width
        composed.height = ic.height
        const cctx = composed.getContext('2d')!
        cctx.drawImage(ic, 0, 0)
        // VistoeraTASK-00430 — composite color-brush strokes (now their
        // own layer) on top of the full-resolution background BEFORE the
        // mask cut, same stacking order as `redrawRight`. `ic` above has
        // just been upgraded to the true full-resolution original (or
        // already was one), so this is always the correct, non-downgraded
        // final pixel data for both layers.
        cctx.drawImage(colorPaintCanvasRef.current, 0, 0)
        cctx.globalCompositeOperation = 'destination-in'
        cctx.drawImage(mc, 0, 0)
        // VistoeraTASK-00598 — bake shape annotations into the export
        // (after mask, before crop) so saved pixels include stroke/fill.
        cctx.globalCompositeOperation = 'source-over'
        drawShapeAnnotations(cctx, annotationsRef.current)
        // VistoeraTASK-00597 — shape-aware alpha punch / keep, written
        // into the real Alpha channel BEFORE bbox extract. Checkerboard
        // is preview-only and never reaches this canvas.
        if (cropNorm) {
          cctx.save()
          applyCropShapeComposite(cctx, cropNorm)
          cctx.restore()
        }
        const out = document.createElement('canvas')
        out.width = sw
        out.height = sh
        const octx = out.getContext('2d')!
        octx.drawImage(composed, sx, sy, sw, sh, 0, 0, sw, sh)
        // VistoeraTASK-00441 root-cause fix — this used to ALWAYS encode
        // as lossless PNG here, regardless of whether the result has any
        // real transparency. The `destination-in` mask-cut above is
        // applied unconditionally, but the mask canvas defaults to
        // fully-opaque white (see `resetMask`), so the overwhelmingly
        // common "no actual cutout, just paint/crop" edit still produced
        // a fully-opaque image wastefully PNG-encoded — real measurement
        // (this task's forensic report) found this inflates a
        // photographic export ~5.5x (a legal ~31MB input → a ~173MB
        // export), the dominant driver of both the reported save slowness
        // and outright save failures for larger images. Any GENUINE
        // transparency (an actual cutout/erase was used) still gets
        // lossless PNG — correctness/quality is never sacrificed, only
        // the wasted encode for the fully-opaque common case is removed.
        // VistoeraTASK-00597 — ellipse/circle/invert ALWAYS require PNG
        // Alpha even if a sampling race somehow missed transparent pixels.
        const { data: outPixels } = octx.getImageData(0, 0, sw, sh)
        const needsAlpha =
          imageDataHasTransparency(outPixels) || cropRequiresTransparency(cropNorm)
        const format = chooseEditedImageExportFormat(needsAlpha)
        // VistoeraTASK-00629 — if the user confirmed a file-size ceiling
        // and has not edited further (exact Blob still stashed) AND this
        // export does not apply an active crop, reuse that Blob so preview
        // and 另存 are byte-identical. Any further edit clears the stash
        // (see pushHistory) and falls through to re-encode + re-compress.
        const maxBytes = exportMaxBytesRef.current
        const stashed = constrainedExportBlobRef.current
        let rawBlob: Blob
        if (stashed && !cropAtExportStart && annotationsRef.current.length === 0) {
          rawBlob = stashed
        } else {
          rawBlob = await new Promise<Blob>((resolve, reject) => {
            const onBlobReady = (b: Blob | null) =>
              b ? resolve(b) : reject(new Error(t('cutoutEditor.exportMaskFailed')))
            if (format.quality !== undefined) out.toBlob(onBlobReady, format.mimeType, format.quality)
            else out.toBlob(onBlobReady, format.mimeType)
          })
          if (maxBytes != null && maxBytes > 0 && rawBlob.size > maxBytes) {
            // Leave headroom for Vistoera metadata injection (~few KB).
            const encodeBudget = Math.max(1024, maxBytes - 4096)
            const compressed = await compressCanvasToMaxBytes({
              source: out,
              maxBytes: encodeBudget,
              hasTransparency: needsAlpha,
            })
            if (!compressed.ok) {
              const err = new Error('FILE_SIZE_LIMIT_UNREACHABLE')
              err.name = 'FileSizeLimitError'
              throw err
            }
            rawBlob = compressed.blob
          }
        }
        // VistoeraTASK-00604 — inject Software/Description/Source into the
        // FINAL encode bytes (no pixel re-draw, no second lossy encode).
        // Survives SiteAsset original upload (server stores bytes as-is).
        const finalBlob = await injectVistoeraExportMetadata(rawBlob)
        if (maxBytes != null && maxBytes > 0 && finalBlob.size > maxBytes) {
          const err = new Error('FILE_SIZE_LIMIT_EXCEEDED')
          err.name = 'FileSizeLimitError'
          throw err
        }
        return finalBlob
        } finally {
          // VistoeraTASK-00504 — always clears, success or failure, so a
          // genuine save failure (already handled/retryable per
          // VistoeraTASK-00441 §11) can never leave the editor's left
          // pane permanently refusing new strokes/drags.
          exportInFlightRef.current = false
        }
      },
      getCropRect: () => cropRef.current,
      clearCrop: () => {
        setCropRect(null)
        pushHistory()
        onDirtyChange?.(true)
        redraw()
      },
      // VistoeraTASK-00616 / 00618 — numeric W/H from props panel.
      // Max size = full image; shift x/y when needed so a larger W/H fits.
      setCropSize: (size, opts) => {
        const cur = cropRef.current
        if (!cur) return null
        const recordHistory = opts?.recordHistory !== false
        const { w: iw, h: ih } = imgSizeRef.current
        if (!(iw > 0 && ih > 0)) return cur
        const next = applyNumericCropSize(cur, size, iw, ih, 4)
        if (
          Math.abs(next.x - cur.x) < 0.01 &&
          Math.abs(next.y - cur.y) < 0.01 &&
          Math.abs(next.w - cur.w) < 0.01 &&
          Math.abs(next.h - cur.h) < 0.01
        ) {
          return cur
        }
        setCropRect(next)
        if (recordHistory) pushHistory()
        onDirtyChange?.(true)
        redraw()
        return next
      },
      confirmCrop,
      clearAnnotations: () => {
        if (annotationsRef.current.length === 0) return
        setAnnotations([])
        pendingShapeRef.current = null
        setSelectedShapeId(null)
        setShapeCtxMenu(null)
        pushHistory()
        onDirtyChange?.(true)
        redraw()
      },
      getAnnotationCount: () => annotationsRef.current.length,
      undo,
      redo,
      invertSelection,
      clearPendingEdits,
      fitToView,
      zoomTo,
      canUndo: () => historyIdx.current > 0,
      canRedo: () => historyIdx.current < history.current.length - 1,
      retrySourceLoad: () => setSourceRetryNonce((n) => n + 1),
      rotateClockwise90,
      resizeTo,
      applyResizedBitmap,
      setExportMaxBytes: (maxBytes) => {
        exportMaxBytesRef.current =
          maxBytes != null && Number.isFinite(maxBytes) && maxBytes > 0 ? Math.floor(maxBytes) : null
      },
      getExportMaxBytes: () => exportMaxBytesRef.current,
      setConstrainedExportBlob: (blob) => {
        constrainedExportBlobRef.current = blob
      },
      getImageSize: () => ({ ...imgSizeRef.current }),
      buildWorkingCompositeCanvas,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      undo,
      redo,
      invertSelection,
      clearPendingEdits,
      confirmCrop,
      fitToView,
      zoomTo,
      pushHistory,
      onDirtyChange,
      redraw,
      cropVersion,
      rotateClockwise90,
      resizeTo,
      applyResizedBitmap,
      buildWorkingCompositeCanvas,
    ],
  )

  // ------------------------------------------------------------------
  // Pointer handling
  // ------------------------------------------------------------------

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceDown.current = true
      // VistoeraTASK-00597 — Shift mid-drag must re-apply aspect lock live
      // without jumping anchors; preventDefault stops browser text-select.
      if (e.key === 'Shift') {
        shiftHeldRef.current = true
        if (tool === 'crop' && cropDrag.current) {
          e.preventDefault()
          applyCropDrag(cropDrag.current.lastImg)
          scheduleRedraw()
        } else if (tool === 'shape' && shapeDragRef.current) {
          e.preventDefault()
          applyShapeDrag(shapeDragRef.current.lastImg)
          scheduleRedraw()
        }
      }
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceDown.current = false
      if (e.key === 'Shift') {
        shiftHeldRef.current = false
        if (tool === 'crop' && cropDrag.current) {
          e.preventDefault()
          applyCropDrag(cropDrag.current.lastImg)
          scheduleRedraw()
        } else if (tool === 'shape' && shapeDragRef.current) {
          e.preventDefault()
          applyShapeDrag(shapeDragRef.current.lastImg)
          scheduleRedraw()
        }
      }
    }
    // VistoeraTASK-00609 — capture-phase: while this editor is mounted,
    // Delete/Backspace must NEVER reach React Flow's node-delete handler.
    // Editable fields keep their normal character delete behavior.
    const onDeleteCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (isCanvasEditableTarget(e.target)) return
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()
      if (tool === 'shape' && selectedShapeIdRef.current) {
        deleteShapeById(selectedShapeIdRef.current)
      }
    }
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (shapeCtxMenu) {
        e.preventDefault()
        e.stopPropagation()
        setShapeCtxMenu(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('keydown', onDeleteCapture, true)
    window.addEventListener('keydown', onEscape, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('keydown', onDeleteCapture, true)
      window.removeEventListener('keydown', onEscape, true)
    }
    // applyCropDrag / scheduleRedraw are stable closures over refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, scheduleRedraw, deleteShapeById, shapeCtxMenu])

  // VistoeraTASK-00597 — when the shell changes shape/invert while a crop
  // already exists, sync meta into the live rect (snap to 1:1 for
  // square/circle) and redraw. Does not pushHistory by itself — the next
  // crop pointer-up / clearCrop / undo boundary still owns the stack.
  useEffect(() => {
    const cur = cropRef.current
    if (!cur) return
    let next: CropRect = normalizeCropRect({
      ...cur,
      shape: cropShape,
      invert: cropInvert,
    })
    if ((cropShape === 'square' || cropShape === 'circle') && Math.abs(next.w - next.h) > 0.5) {
      next = normalizeCropRect({ ...next, ...snapToSquareCentered(next) })
    }
    if (
      next.shape === cur.shape &&
      next.invert === cur.invert &&
      next.x === cur.x &&
      next.y === cur.y &&
      next.w === cur.w &&
      next.h === cur.h
    ) {
      return
    }
    setCropRect(clampCropRect(next))
    redraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropShape, cropInvert])

  function screenToImage(clientX: number, clientY: number, host: HTMLDivElement) {
    const rect = host.getBoundingClientRect()
    // VistoeraTASK-00504 — defensive only (section 四.5 of this task's
    // own audit checklist: NaN/Infinity crop source coordinates). `scale`
    // is already guarded to never be *set* to 0/non-finite by every real
    // caller (`fitToView`/`tryFitWhenHostReady`/`zoomTo` all bail out on
    // a not-yet-ready host rather than committing a degenerate value),
    // so this `Math.max(..., 0.0001)` floor — the exact same one
    // `cropHandleHitRadius` already uses — should normally never engage.
    // Kept as a last-resort guard specifically so a division here can
    // never produce `Infinity`/`NaN`, which (per that section's own
    // checklist) could otherwise flow, unclamped, into a crop rect and
    // ultimately into `exportEditedImageBlob`'s `drawImage`/canvas
    // width/height — never changes on-screen behavior in the normal
    // case since the floor only differs from the real value when the
    // real value was already degenerate.
    const s = Number.isFinite(scaleRef.current) && scaleRef.current > 0 ? scaleRef.current : 0.0001
    return {
      x: (clientX - rect.left - offsetRef.current.x) / s,
      y: (clientY - rect.top - offsetRef.current.y) / s,
    }
  }

  /** Clamp a crop rect to the loaded image's own bounds and enforce a
   * small minimum size so a drag can never collapse it to nothing. */
  function clampCropRect(r: CropRect): CropRect {
    const { w: iw, h: ih } = imgSizeRef.current
    const MIN = 4
    // VistoeraTASK-00504 — defensive only, same rationale as
    // `screenToImage` above: `Math.min`/`Math.max` silently propagate
    // `NaN` (they do NOT fall back to the finite operand), so a
    // non-finite `r.x`/`r.y`/`r.w`/`r.h` reaching this function would
    // otherwise sail straight through every clamp below unchanged. Falls
    // back to a safe `0`-sized corner rather than ever letting a
    // non-finite value reach `exportEditedImageBlob`'s canvas
    // width/height or `drawImage` call.
    const safe = (v: number, fallback: number) => (Number.isFinite(v) ? v : fallback)
    const base = normalizeCropRect(r)
    let x = Math.min(Math.max(0, safe(base.x, 0)), Math.max(0, iw - MIN))
    let y = Math.min(Math.max(0, safe(base.y, 0)), Math.max(0, ih - MIN))
    let w = Math.min(Math.max(MIN, safe(base.w, MIN)), iw - x)
    let h = Math.min(Math.max(MIN, safe(base.h, MIN)), ih - y)
    if (x + w > iw) w = iw - x
    if (y + h > ih) h = ih - y
    // Square/circle must stay 1:1 after edge clamping.
    if (base.shape === 'square' || base.shape === 'circle') {
      const side = Math.min(w, h)
      w = side
      h = side
      if (x + w > iw) x = Math.max(0, iw - w)
      if (y + h > ih) y = Math.max(0, ih - h)
    }
    return normalizeCropRect({ x, y, w, h, shape: base.shape, invert: base.invert })
  }

  /** Handle hit-test radius in IMAGE pixels (derived from a fixed
   * on-screen radius so handles stay easy to grab at any zoom level). */
  function cropHandleHitRadius() {
    return Math.max(6, 10 / Math.max(scaleRef.current, 0.0001))
  }

  /** Which drag mode (if any) a pointer-down at image coords `p` should
   * start, given the currently committed crop rect. Null cropRect means
   * any pointer-down starts a brand-new rect (`'create'`). */
  function hitTestCrop(p: { x: number; y: number }, rect: CropRect | null): { mode: CropDragMode } {
    if (!rect) return { mode: 'create' }
    const hr = cropHandleHitRadius()
    const near = (a: number, b: number) => Math.abs(a - b) <= hr
    const withinX = p.x >= rect.x - hr && p.x <= rect.x + rect.w + hr
    const withinY = p.y >= rect.y - hr && p.y <= rect.y + rect.h + hr
    const onLeftEdge = near(p.x, rect.x) && withinY
    const onRightEdge = near(p.x, rect.x + rect.w) && withinY
    const onTopEdge = near(p.y, rect.y) && withinX
    const onBottomEdge = near(p.y, rect.y + rect.h) && withinX
    if (onTopEdge && onLeftEdge) return { mode: 'resize-nw' }
    if (onTopEdge && onRightEdge) return { mode: 'resize-ne' }
    if (onBottomEdge && onLeftEdge) return { mode: 'resize-sw' }
    if (onBottomEdge && onRightEdge) return { mode: 'resize-se' }
    if (onTopEdge) return { mode: 'resize-n' }
    if (onBottomEdge) return { mode: 'resize-s' }
    if (onLeftEdge) return { mode: 'resize-w' }
    if (onRightEdge) return { mode: 'resize-e' }
    if (p.x >= rect.x && p.x <= rect.x + rect.w && p.y >= rect.y && p.y <= rect.y + rect.h) return { mode: 'move' }
    return { mode: 'create' }
  }

  function applyCropDrag(p: { x: number; y: number }) {
    const drag = cropDrag.current
    if (!drag) return
    drag.lastImg = p
    const { mode, startImg, origRect } = drag
    const dx = p.x - startImg.x
    const dy = p.y - startImg.y
    const shape = origRect?.shape ?? cropShapeRef.current
    const invert = origRect?.invert ?? cropInvertRef.current
    const locked = isAspectLocked(shape, shiftHeldRef.current)
    if (mode === 'create') {
      const geom = rectFromCreateDrag(startImg, p, locked)
      setCropRect(clampCropRect(normalizeCropRect({ ...geom, shape, invert })))
      return
    }
    if (!origRect) return
    let { x, y, w, h } = origRect
    if (mode === 'move') {
      x = origRect.x + dx
      y = origRect.y + dy
    } else {
      if (mode.includes('w')) {
        x = origRect.x + dx
        w = origRect.w - dx
      }
      if (mode.includes('e')) {
        w = origRect.w + dx
      }
      if (mode.includes('n')) {
        y = origRect.y + dy
        h = origRect.h - dy
      }
      if (mode.includes('s')) {
        h = origRect.h + dy
      }
      if (locked) {
        const lockedGeom = enforceAspectRatio({ x, y, w, h }, true, mode)
        x = lockedGeom.x
        y = lockedGeom.y
        w = lockedGeom.w
        h = lockedGeom.h
      }
    }
    setCropRect(clampCropRect(normalizeCropRect({ x, y, w, h, shape, invert })))
  }

  function onLeftPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    // Only the primary (left, button 0) and middle (button 1, pan) buttons
    // are handled here — a stray right-click must never start a stroke.
    if (e.button !== 0 && e.button !== 1) return
    const host = leftHostRef.current!
    // Capture on `currentTarget` (the host div our listeners are actually
    // bound to), not `target` — the inner <canvas> has `pointer-events:
    // none` so target already resolves to the host, but capturing on
    // currentTarget is the correct/robust choice regardless of DOM shape.
    e.currentTarget.setPointerCapture(e.pointerId)
    activePointerId.current = e.pointerId
    setDebugInfo({ tool, polarity, drawing: tool === 'brush', pointerId: e.pointerId, coalesced: 0 })
    if (tool === 'pan' || spaceDown.current || e.button === 1) {
      panning.current = true
      pendingFitRef.current = false
      panStart.current = { x: e.clientX, y: e.clientY, ox: offsetRef.current.x, oy: offsetRef.current.y }
      return
    }
    // Editor Stability P0 (VistoeraTASK-00103) — every branch below reads
    // or writes real pixel data on `imageCanvasRef`/`maskCanvasRef` (paint,
    // `pushHistory()`, or the raw-pixel eyedropper sample). `pixelsReady`
    // is this component's own "safe to touch pixels" signal — it mirrors
    // the instant the Shell's `onReady()` prop fires and its busy spinner
    // disappears (see `onReady?.()` calls below). Swallowing the
    // interaction here, rather than letting a stray click/stroke during
    // the network-fetch-and-decode window reach a still-0×0 canvas, is the
    // actual fix for the intermittent "画布加载失败" Canvas-fatal crash
    // this task root-caused (see `pushHistory`'s doc comment for the full
    // forensic story). Never gated on `tool==='pan'` above — panning never
    // touches pixel data and stays available even while loading. See
    // `canvasPixelOperationAllowed`'s doc comment for the full story.
    if (!pixelsReady || !canvasPixelOperationAllowed(imageCanvasRef.current.width, imageCanvasRef.current.height)) return
    // VistoeraTASK-00504 — see `exportInFlightRef`'s doc comment. A
    // "另存编辑图片" export reads the mask/image canvases (and a crop-rect
    // snapshot) after an await that can genuinely take several seconds
    // on a slow connection; refusing to START a brand-new stroke/drag
    // while that export is still in flight closes the race that could
    // otherwise bake a half-finished, mid-drag mutation into the Blob
    // already being encoded. Panning above is unaffected (never touches
    // pixel data), matching that branch's own existing precedent.
    if (exportInFlightRef.current) return
    // VistoeraTASK-00430 — color-brush no longer needs to wait for the
    // full-resolution original (removed the previous `imageQualityTierRef
    // .current !== 'full'` backstop here). Strokes now paint onto
    // `colorPaintCanvasRef`, a dedicated layer sized correctly (to the
    // TRUE source dimensions) from the very first frame — same timing
    // `maskCanvasRef` already enjoys — and are never at risk of being
    // silently wiped by the later placeholder->full background swap (see
    // that ref's doc comment). The generic `pixelsReady` gate just above
    // is the only precondition color-brush needs now, same as every
    // other tool in this file.
    const p = screenToImage(e.clientX, e.clientY, host)
    if (tool === 'brush') {
      drawing.current = true
      lastPoint.current = p
      paintDot(p.x, p.y)
      onDirtyChange?.(true)
      redraw()
    } else if (tool === 'click') {
      paintDot(p.x, p.y)
      pushHistory()
      onDirtyChange?.(true)
      redraw()
      clickDownScreenPos.current = { x: e.clientX, y: e.clientY }
    } else if (tool === 'rect') {
      rectStart.current = p
      lastPoint.current = p
      drawing.current = true
    } else if (tool === 'lasso') {
      if (!drawing.current) {
        drawing.current = true
        lassoPoints.current = [p]
      } else {
        lassoPoints.current.push(p)
      }
      redrawLeft()
    } else if (tool === 'crop') {
      shiftHeldRef.current = e.shiftKey
      const hit = hitTestCrop(p, cropRef.current)
      // New create-drags start with the shell's current shape/invert; an
      // existing rect keeps its own meta via origRect.
      cropDrag.current = {
        mode: hit.mode,
        startImg: p,
        origRect: cropRef.current
          ? normalizeCropRect(cropRef.current)
          : null,
        lastImg: p,
      }
      drawing.current = true
    } else if (tool === 'shape') {
      // VistoeraTASK-00598 — drag-create; VistoeraTASK-00607 — or move an
      // existing annotation when the pointer is on its stroke (or fill).
      shiftHeldRef.current = e.shiftKey
      let hitAnn: ShapeAnnotation | null = null
      for (let i = annotationsRef.current.length - 1; i >= 0; i--) {
        const ann = annotationsRef.current[i]
        const t = shapeStrokeHitToleranceImgPx(ann.strokeWidth, scaleRef.current)
        if (hitTestShapeAnnotation(p, ann, t)) {
          hitAnn = ann
          break
        }
      }
      if (hitAnn) {
        setSelectedShapeId(hitAnn.id)
        setShapeCtxMenu(null)
        shapeDragRef.current = {
          mode: 'move',
          startImg: p,
          lastImg: p,
          annId: hitAnn.id,
          origAnn: { ...hitAnn },
        }
        setShapeHoverMove(true)
      } else {
        setSelectedShapeId(null)
        setShapeCtxMenu(null)
        shapeDragRef.current = { mode: 'create', startImg: p, lastImg: p }
        applyShapeDrag(p)
      }
      drawing.current = true
      scheduleRedraw()
    } else if (tool === 'eyedropper') {
      // VistoeraTASK-00085 — samples the LEFT pane's base layer (the same
      // thing `redrawLeft` draws underneath the selection-tint overlay —
      // P8: never the tinted `leftViewRef` display canvas, never the
      // checkerboard/bgMode preview fill that only ever exists on the
      // RIGHT pane). Uses the exact same `p = screenToImage(...)` computed
      // above for every other left-pane tool — P9's single canonical
      // coordinate transform, not a second one. Deliberately does NOT set
      // `drawing.current`, call `pushHistory()`, `onDirtyChange?.(true)`,
      // or `redraw()` — P13: sampling a color can never mutate the image
      // or enter the undo stack.
      //
      // VistoeraTASK-00430 — the base layer is now TWO canvases
      // (`imageCanvasRef`'s background tier + `colorPaintCanvasRef`'s
      // strokes on top), so a single `getImageData` against just the
      // background would miss/misreport any color-brush paint already
      // sitting on top of this exact pixel. Manually source-over-composite
      // the two 1×1 samples instead of allocating a scratch canvas for a
      // single pixel.
      const ic = imageCanvasRef.current
      const paint = colorPaintCanvasRef.current
      const px = Math.floor(p.x)
      const py = Math.floor(p.y)
      if (px < 0 || py < 0 || px >= ic.width || py >= ic.height) {
        onEyedropperSample?.(null)
      } else {
        const bg = ic.getContext('2d')!.getImageData(px, py, 1, 1).data
        const fg = paint.getContext('2d')!.getImageData(px, py, 1, 1).data
        const fgA = fg[3] / 255
        const bgA = bg[3] / 255
        const outA = fgA + bgA * (1 - fgA)
        const a = Math.round(outA * 255)
        // P11 — a fully transparent composite pixel has no meaningful RGB
        // (canvas `getImageData` returns `[0,0,0,0]` there regardless of
        // whatever `bgMode` preview color happens to show through it);
        // report `null` rather than fabricating a color from it.
        if (outA <= 0) {
          onEyedropperSample?.(null)
        } else {
          const blend = (fgC: number, bgC: number) => Math.round((fgC * fgA + bgC * bgA * (1 - fgA)) / outA)
          onEyedropperSample?.({ r: blend(fg[0], bg[0]), g: blend(fg[1], bg[1]), b: blend(fg[2], bg[2]), a })
        }
      }
    }
  }

  function onLeftPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const host = leftHostRef.current!
    const hostRect = host.getBoundingClientRect()
    setBrushCursorPos({ x: e.clientX - hostRect.left, y: e.clientY - hostRect.top })

    // Click tool ("AI point prompt") never paints on move by design — see
    // `onClickToolDragAttempt` prop docs. This purely observes screen-space
    // drag distance to fire a one-shot UX hint; it never sets `drawing`,
    // never touches the mask, and does not affect any other tool.
    if (
      tool === 'click' &&
      clickDownScreenPos.current &&
      !clickDragHintFired.current &&
      (e.buttons & 1) === 1
    ) {
      const dx = e.clientX - clickDownScreenPos.current.x
      const dy = e.clientY - clickDownScreenPos.current.y
      if (Math.hypot(dx, dy) > 6) {
        clickDragHintFired.current = true
        onClickToolDragAttempt?.()
      }
    }

    // Defensive: if the left mouse button was released without a
    // pointerup/pointercancel/lostpointercapture ever reaching us (can
    // happen if focus is stolen mid-drag, e.g. by an OS-level dialog),
    // `drawing.current` would otherwise stay stuck `true` forever and the
    // next click would silently continue "drawing" from a stale point.
    if (drawing.current && (e.buttons & 1) === 0 && (e.buttons & 4) === 0) {
      if (tool === 'brush') pushHistory(brushMode === 'color')
      if (tool === 'crop') cropDrag.current = null
      if (tool === 'shape') {
        restoreShapeMoveIfNeeded()
        shapeDragRef.current = null
        pendingShapeRef.current = null
        scheduleRedraw()
      }
      drawing.current = false
      panning.current = false
      lastPoint.current = null
      setDebugInfo((d) => ({ ...d, drawing: false }))
      return
    }

    if (panning.current) {
      setOffset({
        x: panStart.current.ox + (e.clientX - panStart.current.x),
        y: panStart.current.oy + (e.clientY - panStart.current.y),
      })
      return
    }

    // VistoeraTASK-00607 — hover move cursor when over a shape stroke/fill
    // (only while idle; during create keep crosshair).
    if (tool === 'shape' && !drawing.current) {
      const hp = screenToImage(e.clientX, e.clientY, host)
      let hover = false
      for (let i = annotationsRef.current.length - 1; i >= 0; i--) {
        const ann = annotationsRef.current[i]
        const t = shapeStrokeHitToleranceImgPx(ann.strokeWidth, scaleRef.current)
        if (hitTestShapeAnnotation(hp, ann, t)) {
          hover = true
          break
        }
      }
      if (hover !== shapeHoverMove) setShapeHoverMove(hover)
    } else if (tool !== 'shape' && shapeHoverMove) {
      setShapeHoverMove(false)
    }

    if (!drawing.current) return
    if (tool === 'brush' && lastPoint.current) {
      // `getCoalescedEvents` recovers every raw sample the OS/browser
      // actually recorded since the last dispatched pointermove (browsers
      // batch several per animation frame and normally only deliver the
      // last one). Without this, a fast *curved* drag would only give us
      // the sparse endpoints and straight-line interpolation between them
      // would visibly "cut corners" — the polyline/faceted look reported.
      const native = e.nativeEvent as PointerEvent & { getCoalescedEvents?: () => PointerEvent[] }
      const coalesced = native.getCoalescedEvents?.() ?? []
      const samples = coalesced.length > 0 ? coalesced : [native]
      const r = Math.max(1, brushSize / 2)
      // Stamp spacing tuned to a fraction of the brush *radius* (not
      // diameter) so stamps overlap densely enough that the union edge
      // reads as one smooth stroke instead of a chain of visible bumps —
      // especially important for soft/low-hardness brushes.
      const spacing = Math.max(1, r * 0.18)
      let last = lastPoint.current
      for (const s of samples) {
        const p = screenToImage(s.clientX, s.clientY, host)
        const dist = Math.hypot(p.x - last.x, p.y - last.y)
        const steps = Math.max(1, Math.ceil(dist / spacing))
        for (let i = 1; i <= steps; i++) {
          const t = i / steps
          paintDot(last.x + (p.x - last.x) * t, last.y + (p.y - last.y) * t)
        }
        last = p
      }
      lastPoint.current = last
      setDebugInfo((d) => ({ ...d, coalesced: samples.length }))
      scheduleRedraw()
    } else if (tool === 'rect') {
      const p = screenToImage(e.clientX, e.clientY, host)
      lastPoint.current = p
      redrawLeft()
    } else if (tool === 'lasso') {
      const p = screenToImage(e.clientX, e.clientY, host)
      lastPoint.current = p
      redrawLeft()
    } else if (tool === 'crop' && cropDrag.current) {
      // VistoeraTASK-00504 — rAF-coalesced redraw (do NOT sync full-res
      // double-canvas composite on every raw pointermove).
      // VistoeraTASK-00597 — also refresh Shift from the event so mid-drag
      // aspect lock tracks the OS key state even before keydown fires.
      shiftHeldRef.current = e.shiftKey
      const p = screenToImage(e.clientX, e.clientY, host)
      applyCropDrag(p)
      scheduleRedraw()
    } else if (tool === 'shape' && shapeDragRef.current) {
      shiftHeldRef.current = e.shiftKey
      const p = screenToImage(e.clientX, e.clientY, host)
      applyShapeDrag(p)
      scheduleRedraw()
    }
  }

  function releaseLeftCapture(e?: React.PointerEvent<HTMLDivElement>) {
    const id = e?.pointerId ?? activePointerId.current
    activePointerId.current = null
    if (id == null) return
    try {
      leftHostRef.current?.releasePointerCapture(id)
    } catch {
      // already released — safe to ignore
    }
  }

  function onLeftPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    releaseLeftCapture(e)
    clickDownScreenPos.current = null
    setDebugInfo((d) => ({ ...d, drawing: false, pointerId: null }))
    if (panning.current) {
      panning.current = false
      return
    }
    if (tool === 'brush' && drawing.current) {
      // The whole stroke — from pointerdown through every interpolated
      // pointermove — collapses into exactly one history entry here, so a
      // single "undo" reverts the entire brush stroke, not one dab of it.
      drawing.current = false
      lastPoint.current = null
      pushHistory(brushMode === 'color')
    } else if (tool === 'rect' && drawing.current && rectStart.current && lastPoint.current) {
      const a = rectStart.current
      const b = lastPoint.current
      fillShape((ctx) => {
        ctx.fillRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y))
      })
      drawing.current = false
      rectStart.current = null
      lastPoint.current = null
      pushHistory()
      onDirtyChange?.(true)
      redraw()
    } else if (tool === 'crop' && drawing.current) {
      drawing.current = false
      cropDrag.current = null
      // A crop rect that never grew past the minimum (e.g. an accidental
      // single click) is treated as "no crop", matching P22's plain
      // drag-create expectation rather than leaving a degenerate 4×4 box.
      if (cropRef.current && cropRef.current.w <= 4 && cropRef.current.h <= 4) {
        setCropRect(null)
      }
      pushHistory()
      onDirtyChange?.(true)
      redraw()
    } else if (tool === 'shape' && drawing.current) {
      drawing.current = false
      const drag = shapeDragRef.current
      shapeDragRef.current = null
      if (drag?.mode === 'move' && drag.annId && drag.origAnn) {
        const cur = annotationsRef.current.find((a) => a.id === drag.annId)
        const moved =
          cur != null &&
          (Math.abs(cur.x - drag.origAnn.x) > 0.5 || Math.abs(cur.y - drag.origAnn.y) > 0.5)
        // Sync React + one history entry for the whole drag (never per-move).
        setAnnotations(cloneAnnotations(annotationsRef.current))
        if (moved) {
          pushHistory()
          onDirtyChange?.(true)
        }
      } else {
        const pending = pendingShapeRef.current
        pendingShapeRef.current = null
        if (pending && pending.w >= 4 && pending.h >= 4) {
          const committed: ShapeAnnotation = {
            ...pending,
            id: `shape-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          }
          setAnnotations([...annotationsRef.current, committed])
          pushHistory()
          onDirtyChange?.(true)
        }
      }
      pendingShapeRef.current = null
      redraw()
    }
  }

  function onLeftPointerCancel(e: React.PointerEvent<HTMLDivElement>) {
    releaseLeftCapture(e)
    clickDownScreenPos.current = null
    // A stroke that was actually in progress still counts as a completed
    // edit — otherwise a cancelled pointer (e.g. palm rejection, OS
    // gesture interrupt) would silently discard paint the user already
    // committed to the mask canvas.
    if (drawing.current && tool === 'brush') pushHistory(brushMode === 'color')
    if (drawing.current && tool === 'shape') {
      restoreShapeMoveIfNeeded()
      pendingShapeRef.current = null
      shapeDragRef.current = null
      scheduleRedraw()
    }
    panning.current = false
    drawing.current = false
    lastPoint.current = null
    rectStart.current = null
    lassoPoints.current = []
    cropDrag.current = null
    setDebugInfo((d) => ({ ...d, drawing: false, pointerId: null }))
  }

  // `pointercancel` already covers the common interruption cases, but
  // `lostpointercapture` is the one event that's guaranteed to fire
  // whenever capture ends for *any* reason (including some the spec
  // doesn't require pointercancel for) — cheap extra safety net so a
  // stroke can never get stuck "in progress" indefinitely.
  function onLeftLostPointerCapture(e: React.PointerEvent<HTMLDivElement>) {
    if (drawing.current || panning.current) onLeftPointerCancel(e)
  }

  // Intentionally does nothing to `brushCursorPos`: the overlay should
  // freeze in place (not vanish) when the pointer leaves the canvas to
  // click a toolbar button or drag a slider — see the comment on
  // `brushCursorPos`'s declaration for the full rationale. Kept as a named
  // no-op (rather than removing the prop) to document that this was a
  // deliberate choice, not an oversight.
  function onLeftPointerLeave() {}

  /** VistoeraTASK-00609 — editor-local shape context menu (never Canvas node menu). */
  function onLeftContextMenu(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (tool !== 'shape') {
      setShapeCtxMenu(null)
      return
    }
    const host = leftHostRef.current
    if (!host) return
    const p = screenToImage(e.clientX, e.clientY, host)
    let hitAnn: ShapeAnnotation | null = null
    for (let i = annotationsRef.current.length - 1; i >= 0; i--) {
      const ann = annotationsRef.current[i]
      const tol = shapeStrokeHitToleranceImgPx(ann.strokeWidth, scaleRef.current)
      if (hitTestShapeAnnotation(p, ann, tol)) {
        hitAnn = ann
        break
      }
    }
    if (!hitAnn) {
      setShapeCtxMenu(null)
      return
    }
    setSelectedShapeId(hitAnn.id)
    // Keep menu inside viewport
    const pad = 8
    const menuW = 180
    const menuH = 40
    const x = Math.min(e.clientX, window.innerWidth - menuW - pad)
    const y = Math.min(e.clientY, window.innerHeight - menuH - pad)
    setShapeCtxMenu({ x: Math.max(pad, x), y: Math.max(pad, y), annId: hitAnn.id })
  }

  useEffect(() => {
    if (!shapeCtxMenu) return
    const onDocPointer = (ev: MouseEvent) => {
      const el = ev.target as HTMLElement | null
      if (el?.closest('[data-testid="image-editor-shape-ctx-menu"]')) return
      setShapeCtxMenu(null)
    }
    document.addEventListener('mousedown', onDocPointer, true)
    return () => document.removeEventListener('mousedown', onDocPointer, true)
  }, [shapeCtxMenu])

  function onLeftDoubleClick() {
    if (tool === 'lasso' && drawing.current && lassoPoints.current.length > 2) {
      const pts = lassoPoints.current
      fillShape((ctx) => {
        ctx.beginPath()
        ctx.moveTo(pts[0].x, pts[0].y)
        for (const pt of pts.slice(1)) ctx.lineTo(pt.x, pt.y)
        ctx.closePath()
        ctx.fill()
      })
      drawing.current = false
      lassoPoints.current = []
      pushHistory()
      onDirtyChange?.(true)
      redraw()
    }
  }

  // The right (transparent-result) pane is view-only: it never hosts a
  // drawing tool, so a plain left-button drag always pans — the user should
  // never have to switch to a "pan tool" just to look around the image.
  // Wheel zoom uses native non-passive listeners (see useEffect above);
  // React 19's delegated onWheel is passive at the root.
  function onRightPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0 && e.button !== 1) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    rightPanning.current = true
    setRightDragging(true)
    pendingFitRef.current = false
    panStart.current = { x: e.clientX, y: e.clientY, ox: offsetRef.current.x, oy: offsetRef.current.y }
  }
  function onRightPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!rightPanning.current) return
    setOffset({
      x: panStart.current.ox + (e.clientX - panStart.current.x),
      y: panStart.current.oy + (e.clientY - panStart.current.y),
    })
  }
  function endRightPan() {
    rightPanning.current = false
    setRightDragging(false)
  }

  // Unlike the toolbar/properties panel (where the brush overlay should
  // freeze in place, not vanish — see `brushCursorPos`), the right pane is
  // a different *canvas context* entirely: it pans/views but never
  // paints, so a leftover brush circle floating over it would misleadingly
  // suggest painting is possible there. Explicitly clear on entry.
  function onRightPointerEnter() {
    setBrushCursorPos(null)
  }

  const isBrushTool = tool === 'brush'
  // The "click" tool (AI foreground/background point prompt) is a
  // fundamentally different interaction — one placed point, not a
  // brush-diameter stroke — so it gets its own fixed-size indicator
  // instead of either the brush circle or a bare system crosshair. Mutually
  // exclusive with the brush cursor by construction: a tool can never be
  // both 'brush' and 'click' at once.
  const isClickTool = tool === 'click'
  // Once the brush/point tool has its own overlay, the native OS cursor
  // would just be visual noise sitting on top of it. rect/lasso keep the
  // system crosshair as a lightweight shape-drawing affordance.
  const shapeMoving = shapeDragRef.current?.mode === 'move' && drawing.current
  const cursor =
    tool === 'none'
      ? 'default'
      : tool === 'pan' || spaceDown.current
        ? 'grab'
        : isBrushTool || isClickTool
          ? 'none'
          : tool === 'shape' && (shapeHoverMove || shapeMoving)
            ? 'move'
            : 'crosshair'
  const brushScreenDiameter = Math.max(2, brushSize * scale)
  const showBrushCursor = isBrushTool && brushCursorPos !== null
  const showClickCursor = isClickTool && brushCursorPos !== null

  return (
    <div className="dual-pane">
      <div className="dual-pane-half">
        <div className="dual-pane-label">{t('cutoutEditor.sourceAndSelection')}</div>
        <div
          ref={leftHostRef}
          className="mask-canvas-host"
          style={{ cursor }}
          onPointerDown={onLeftPointerDown}
          onPointerMove={onLeftPointerMove}
          onPointerUp={onLeftPointerUp}
          onPointerCancel={onLeftPointerCancel}
          onPointerLeave={onLeftPointerLeave}
          onLostPointerCapture={onLeftLostPointerCapture}
          onDoubleClick={onLeftDoubleClick}
          onContextMenu={onLeftContextMenu}
          onDragStart={(e) => e.preventDefault()}
        >
          <canvas ref={leftViewRef} style={{ opacity: sourceReady ? 1 : 0 }} />
          {!sourceReady && displayLeftUrl && (
            <img
              ref={leftProgRef}
              src={displayLeftUrl}
              alt={t('cutoutEditor.sourcePreviewAlt')}
              className="dual-pane-progressive-img"
              data-testid="cutout-progressive-left"
              draggable={false}
              onLoad={onProgressiveLeftLoad}
            />
          )}
          {showBrushCursor && (
            <div
              // VistoeraTASK-00080 — while `brushMode==='color'`, the
              // cursor shows the ACTUAL `brushColor` about to be painted
              // (border/dot both driven by inline style below) instead of
              // the fixed green/red "add"/"subtract" selection palette,
              // which would otherwise be misleading (color brush is
              // neither "keep" nor "erase" — `polarity` is inert in this
              // mode, see `paintColorDot`'s doc comment).
              className={`brush-cursor ${brushMode === 'color' ? 'brush-cursor-color' : polarity === 'add' ? 'brush-cursor-add' : 'brush-cursor-subtract'}`}
              style={{
                left: brushCursorPos!.x,
                top: brushCursorPos!.y,
                width: brushScreenDiameter,
                height: brushScreenDiameter,
                ...(brushMode === 'color' ? { borderColor: brushColor, background: `${brushColor}1f` } : null),
              }}
            >
              <span className="brush-cursor-dot" style={brushMode === 'color' ? { background: brushColor } : undefined} />
            </div>
          )}
          {showClickCursor && (
            <div
              className={`point-cursor ${polarity === 'add' ? 'point-cursor-add' : 'point-cursor-subtract'}`}
              style={{ left: brushCursorPos!.x, top: brushCursorPos!.y }}
              data-testid="point-select-cursor"
            >
              <span className="point-cursor-ring" />
              <span className="point-cursor-dot" />
            </div>
          )}
          {debugMode && (
            <div className="editor-debug-readout" data-testid="editor-debug-readout">
              <div>tool={debugInfo.tool || tool}</div>
              <div>operation={debugInfo.polarity === 'add' ? 'keep(+255)' : 'remove(-255→0)'}</div>
              <div>polarity={debugInfo.polarity || polarity}</div>
              <div>drawing={String(debugInfo.drawing)}</div>
              <div>pointerId={String(debugInfo.pointerId)}</div>
              <div>coalescedSamples={debugInfo.coalesced}</div>
              <div>scale={scale.toFixed(2)}</div>
            </div>
          )}
        </div>
      </div>
      <div className="dual-pane-divider" />
      <div className="dual-pane-half">
        <div className="dual-pane-label">{resultPaneLabel ?? t('cutoutEditor.transparentPreviewLabel')}</div>
        <div
          ref={rightHostRef}
          className={`mask-canvas-host mask-canvas-host-pan ${bgMode === 'checker' ? 'checkerboard' : ''}`}
          style={{ cursor: rightDragging ? 'grabbing' : 'grab' }}
          onPointerEnter={onRightPointerEnter}
          onPointerDown={onRightPointerDown}
          onPointerMove={onRightPointerMove}
          onPointerUp={endRightPan}
          onPointerCancel={endRightPan}
          onContextMenu={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setShapeCtxMenu(null)
          }}
          onDragStart={(e) => e.preventDefault()}
        >
          <canvas ref={rightViewRef} style={{ opacity: pixelsReady ? 1 : 0 }} />
          {!pixelsReady && displayRightUrl && (
            <img
              ref={rightProgRef}
              src={displayRightUrl}
              alt={resultPaneLabel ?? t('cutoutEditor.transparentPreviewAlt')}
              className="dual-pane-progressive-img"
              data-testid="cutout-progressive-right"
              draggable={false}
              onLoad={onProgressiveRightLoad}
            />
          )}
        </div>
      </div>
      {shapeCtxMenu
        ? createPortal(
            <div
              className="pcv-context-menu-shell image-editor-shape-ctx-menu-shell"
              style={{ position: 'fixed', left: shapeCtxMenu.x, top: shapeCtxMenu.y, zIndex: 11000 }}
              data-testid="image-editor-shape-ctx-menu"
              onContextMenu={(e) => e.preventDefault()}
            >
              <div className="pcv-context-menu pcv-context-menu-root image-editor-shape-ctx-menu">
                <button
                  type="button"
                  className="pcv-context-menu-item pcv-context-menu-item--danger"
                  data-testid="image-editor-shape-ctx-delete"
                  onClick={() => {
                    const id = shapeCtxMenu.annId
                    setShapeCtxMenu(null)
                    deleteShapeById(id)
                  }}
                >
                  {t('imageEditor.deleteThisShapeBrush', { ns: 'canvas' })}
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
})

export default DualPaneCutoutEditor

/** VistoeraTASK-00557 — rotates a canvas's OWN current pixel content by
 * `steps × 90°` clockwise, in place (resizing it to the swapped
 * dimensions on an odd step count). Pixel-exact/lossless for any multiple
 * of 90° — every destination pixel maps to exactly one source pixel, no
 * resampling/interpolation — so repeated calls never degrade quality
 * (四.5) and a net multiple-of-4 total is always a byte-for-byte
 * identity (四.4, verified by `rotateCanvasContentBy90.contract.test.ts`
 * via the geometry below, not a runtime canvas assertion — no jsdom
 * Canvas 2D harness in this repo). `steps` is normalized into `[0, 4)`
 * first, so a `0`/multiple-of-4 input is a true no-op (canvas never
 * touched, not even resized) — the common case for every undo/redo step
 * that does NOT cross a rotation boundary. */
function rotateCanvasContentBy90(canvas: HTMLCanvasElement, steps: number): void {
  const n = ((Math.trunc(steps) % 4) + 4) % 4
  for (let i = 0; i < n; i += 1) {
    const out = document.createElement('canvas')
    out.width = canvas.height
    out.height = canvas.width
    const octx = out.getContext('2d')!
    octx.translate(out.width, 0)
    octx.rotate(Math.PI / 2)
    octx.drawImage(canvas, 0, 0)
    canvas.width = out.width
    canvas.height = out.height
    canvas.getContext('2d')!.drawImage(out, 0, 0)
  }
}

/** VistoeraTASK-00557 (§五/§十二) — paints a PRISTINE, never-rotated
 * source (`img`, the placeholder or full-resolution original) into
 * `ctx` already in the CURRENT cumulative rotation (`rotationDeg`, one
 * of 0/90/180/270) every other backing canvas is in, stretch-fit to the
 * (already rotation-swapped, if applicable) `destW × destH` box.
 *
 * Both the initial/background-tier paint path (`paintImageTier`) AND the
 * export's own full-resolution tier-upgrade funnel through this ONE
 * function so the two can never silently drift out of sync (§六's "must
 * not implement two separate, inconsistent rotation logics"). At
 * `rotationDeg === 0` this is byte-for-byte the original unconditional
 * `ctx.drawImage(img, 0, 0, destW, destH)` call it replaces — zero
 * behavior change for every session that never rotates. */
function drawPristineImageRotated(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  rotationDeg: number,
  destW: number,
  destH: number,
): void {
  const steps = ((Math.round(rotationDeg / 90) % 4) + 4) % 4
  if (steps === 0) {
    ctx.drawImage(img, 0, 0, destW, destH)
    return
  }
  const swapped = steps === 1 || steps === 3
  const tmp = document.createElement('canvas')
  tmp.width = swapped ? destH : destW
  tmp.height = swapped ? destW : destH
  tmp.getContext('2d')!.drawImage(img, 0, 0, tmp.width, tmp.height)
  rotateCanvasContentBy90(tmp, steps)
  ctx.drawImage(tmp, 0, 0)
}

function drawRectPreview(
  ctx: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
) {
  ctx.save()
  ctx.strokeStyle = '#00e0ff'
  ctx.lineWidth = 1.5
  ctx.setLineDash([6, 4])
  ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y))
  ctx.restore()
}

/** VistoeraTASK-00597 — fill the closed crop shape path (rect or ellipse). */
function fillCropShapePath(ctx: CanvasRenderingContext2D, rect: CropRect) {
  const { x, y, w, h, shape } = normalizeCropRect(rect)
  ctx.beginPath()
  if (isEllipseLike(shape)) {
    ctx.ellipse(x + w / 2, y + h / 2, Math.max(0, w / 2), Math.max(0, h / 2), 0, 0, Math.PI * 2)
  } else {
    ctx.rect(x, y, w, h)
  }
  ctx.fill()
}

/** VistoeraTASK-00597 — apply keep-interior / punch-interior to a canvas. */
function applyCropShapeComposite(ctx: CanvasRenderingContext2D, rect: CropRect) {
  const crop = normalizeCropRect(rect)
  ctx.fillStyle = '#fff'
  if (crop.invert) {
    ctx.globalCompositeOperation = 'destination-out'
  } else {
    ctx.globalCompositeOperation = 'destination-in'
  }
  fillCropShapePath(ctx, crop)
}

/** Crop overlay (P22 + VistoeraTASK-00597): dim excluded pixels, draw the
 * true shape border (ellipse/circle are real ellipses, never a fake rect
 * stroke), bbox handles for resize, and a live size label. */
function drawCropOverlay(
  ctx: CanvasRenderingContext2D,
  rect: CropRect,
  canvasW: number,
  canvasH: number,
  scale: number,
  opts?: { borderColor?: string; selectionOpacity?: number },
) {
  const crop = normalizeCropRect(rect)
  const { x, y, w, h, shape, invert } = crop
  const s = Math.max(scale, 0.0001)
  const borderColor = opts?.borderColor || '#3b82f6'
  const dimA = Math.min(0.85, Math.max(0.15, opts?.selectionOpacity ?? 0.6))
  ctx.save()
  ctx.fillStyle = `rgba(0, 0, 0, ${dimA})`
  if (invert) {
    // Dim the punched interior so the user sees what becomes transparent.
    fillCropShapePath(ctx, crop)
  } else {
    ctx.beginPath()
    ctx.rect(0, 0, canvasW, canvasH)
    if (isEllipseLike(shape)) {
      ctx.ellipse(x + w / 2, y + h / 2, Math.max(0, w / 2), Math.max(0, h / 2), 0, 0, Math.PI * 2)
    } else {
      ctx.rect(x, y, w, h)
    }
    ctx.fill('evenodd')
  }
  ctx.strokeStyle = borderColor
  ctx.lineWidth = 1.5 / s
  ctx.setLineDash([6 / s, 4 / s])
  ctx.beginPath()
  if (isEllipseLike(shape)) {
    ctx.ellipse(x + w / 2, y + h / 2, Math.max(0, w / 2), Math.max(0, h / 2), 0, 0, Math.PI * 2)
  } else {
    ctx.rect(x, y, w, h)
  }
  ctx.stroke()
  ctx.setLineDash([])
  // Bounding-box handles (resize still uses the axis-aligned bbox).
  const hs = 8 / s
  ctx.fillStyle = borderColor
  const handles: Array<[number, number]> = [
    [x, y],
    [x + w / 2, y],
    [x + w, y],
    [x, y + h / 2],
    [x + w, y + h / 2],
    [x, y + h],
    [x + w / 2, y + h],
    [x + w, y + h],
  ]
  for (const [hx, hy] of handles) {
    ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs)
  }
  // Live size label above the bbox (matches confirmed UI).
  const label = `${Math.round(w)} × ${Math.round(h)}`
  const fontPx = Math.max(11, 12 / s)
  ctx.font = `600 ${fontPx}px ui-sans-serif, system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'bottom'
  const lx = x + w / 2
  const ly = y - 6 / s
  const tw = ctx.measureText(label).width
  const padX = 6 / s
  const padY = 3 / s
  ctx.fillStyle = 'rgba(15, 23, 42, 0.78)'
  ctx.fillRect(lx - tw / 2 - padX, ly - fontPx - padY, tw + padX * 2, fontPx + padY * 2)
  ctx.fillStyle = '#ffffff'
  ctx.fillText(label, lx, ly)
  ctx.restore()
}

function drawLassoPreview(ctx: CanvasRenderingContext2D, pts: Array<{ x: number; y: number }>) {
  ctx.save()
  ctx.strokeStyle = '#00e0ff'
  ctx.lineWidth = 1.5
  ctx.setLineDash([6, 4])
  ctx.beginPath()
  ctx.moveTo(pts[0].x, pts[0].y)
  for (const p of pts.slice(1)) ctx.lineTo(p.x, p.y)
  ctx.stroke()
  ctx.restore()
}

/** 1px highlighted contour along the current selection boundary (edge of the
 * red overlay), computed from the mask canvas's alpha channel via a cheap
 * dilate-minus-erode approximation. Kept subtle so it reads as a fine line,
 * not a thick outline. */
function drawMaskEdge(ctx: CanvasRenderingContext2D, mc: HTMLCanvasElement) {
  if (mc.width > 3000 || mc.height > 3000) return // skip on very large canvases for performance
  const mctx = mc.getContext('2d')!
  const { width: w, height: h } = mc
  const src = mctx.getImageData(0, 0, w, h).data
  const edge = ctx.createImageData(w, h)
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h ? 0 : src[(y * w + x) * 4 + 3])
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const c = at(x, y)
      const isEdge =
        c > 40 &&
        (at(x - 1, y) <= 40 || at(x + 1, y) <= 40 || at(x, y - 1) <= 40 || at(x, y + 1) <= 40)
      if (isEdge) {
        const i = (y * w + x) * 4
        edge.data[i] = 255
        edge.data[i + 1] = 255
        edge.data[i + 2] = 255
        edge.data[i + 3] = 200
      }
    }
  }
  const tmp = document.createElement('canvas')
  tmp.width = w
  tmp.height = h
  tmp.getContext('2d')!.putImageData(edge, 0, 0)
  // The edge test above is a hard per-pixel threshold (4-neighborhood),
  // which by construction follows the mask's raster grid exactly — at
  // high zoom that reads as a visible staircase. A small blur turns the
  // binary 1px contour into an anti-aliased line, which is what a "smooth
  // boundary" actually looks like once magnified; it costs nothing
  // functionally since this line is cosmetic only (the real mask data
  // driving keep/remove and the transparent preview is unaffected).
  ctx.save()
  ctx.filter = 'blur(0.8px)'
  ctx.drawImage(tmp, 0, 0)
  ctx.restore()
}

// `loadHtmlImage` now lives in `./mediaLoader` (imported above) — Editor
// Load Performance P0 (2026-08) consolidated this file's copy and
// `backgroundPresets.ts`'s identical copy into one shared loader.
